// @ts-check
/**
 * Birch AI Companion — chat widget custom element.
 *
 * Persistence:
 *   - Session id and lightweight message log live in localStorage so the chat
 *     survives page refreshes and tab restarts (item #5). The full transcript
 *     is rehydrated from D1 (/apps/ai/sessions/:id) on mount, so localStorage
 *     only needs ids and a short replay buffer.
 *   - Reset (↻) icon clears the local session and starts a fresh sessionId.
 *
 * Birch vs LushLife products (items #6, #7, #9):
 *   - Birch product cards are NOT rendered inline in the chat. The chat shows
 *     only the LLM's descriptive text. On the FIRST card of a Birch search the
 *     widget auto-redirects to /search?view=ai&session=<id> so the cards show
 *     on the dedicated search page. A "Show results ›" box is appended in the
 *     chat for replayability — clicking re-opens the same results page.
 *   - LushLife (external) cards render inline in the chat — they're not a
 *     Birch catalog and have no /search page representation.
 *
 * Nav-search unification (items #1, #8):
 *   - Window event `birch:open-with-query { query, fresh: true }` opens the
 *     chat with a fresh sessionId and sends the query. The nav overlay
 *     dispatches this on Enter so search-box queries always start a new chat.
 *     `fullscreen: true` additionally expands the panel (desktop; mobile is
 *     already full-screen) for surfaces where the chat IS the task — the
 *     registry builder handoff from /pages/registry-start.
 */

import { emptyStateSuggestions } from './birch-ai-suggestions.js';
import { cartDisplayCount } from './cart-count.js';

const BirchSpeechRecognitionCtor = /** @type {any} */ (window).SpeechRecognition || /** @type {any} */ (window).webkitSpeechRecognition || null;
const SpeechSynthesisConstructor = window.speechSynthesis || null;

/** @type {string} */
const PROXY = /** @type {any} */ (window).BirchAI?.routes?.aiProxy ?? '/apps/ai';

/**
 * Installed-PWA detection (home-screen installs). Stamps `birch-standalone` on <html>
 * when the store is running as a home-screen app, plus `birch-standalone--ios`
 * for iOS specifically — there the page extends under the home indicator and
 * `env(safe-area-inset-bottom)` reads 0 (the theme's viewport meta has no
 * viewport-fit=cover), so the input row needs a hard-coded bottom gap.
 * `navigator.standalone` only exists on iOS WebKit.
 */
(function markStandalone() {
  const nav = /** @type {any} */ (navigator);
  const ios = nav.standalone === true;
  const standalone = ios || (window.matchMedia?.('(display-mode: standalone)').matches ?? false);
  if (!standalone) return;
  document.documentElement.classList.add('birch-standalone');
  if (ios) document.documentElement.classList.add('birch-standalone--ios');
})();

/**
 * Tracker / log pages — surfaces with no shelf on them. Mirrors
 * TRACKER_PAGE_HANDLES in the worker's tools/product/search/shopping-intent.ts,
 * which is what actually decides whether the "Find external products" offer is
 * emitted. Duplicated here on purpose: the theme and the worker deploy
 * independently, and a parent reading their own sleep log should not be offered
 * a web search for it just because one of the two is a release behind.
 * @type {ReadonlySet<string>}
 */
const TRACKER_PAGE_HANDLES = new Set([
  'sleep-tracker', 'feeding-tracker', 'growth-tracker', 'pregnancy-tracker',
  'parenting-tracker', 'tracker', 'browse-trackers', 'milestones',
  'baby-log', 'baby-brief',
]);

const SESSION_KEY = 'birch_ai_session';
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — survives refreshes and overnight returns
// This ID exists solely for the consented Juno demand signal. It is never sent
// to Juno directly: the signed Ask Birch edge worker HMAC-pseudonymizes it.
const JUNO_VISITOR_KEY = 'birch_juno_visitor_v1';
// One pending product-card opening. It survives the same-tab navigation from a
// chat card to its PDP, but never leaves the browser except as its opaque Juno
// interaction id after Shopify analytics consent has been re-checked.
const JUNO_DWELL_KEY = 'birch_juno_dwell_v1';
const DRAFT_KEY = 'birch_ai_draft';              // tiny in-flight render log (text + LushLife cards)
const OPEN_STATE_KEY = 'birch_ai_open';          // last-seen open state, so panel reopens after navigation
const SEARCH_RESULTS_PATH = '/search';
// The "Open full page (N)" box under a Birch result set is retired — the inline
// cards ARE the result. Kept behind a flag (rather than deleted) because the
// results page it links to is still live and reachable by URL; flip to true to
// bring the entry point back. Suppressing it here also covers the replay path,
// so rehydrated transcripts don't resurrect the box.
const SHOW_RESULTS_BOX_ENABLED = false;

/** @param {boolean} open */
function persistOpenState(open) {
  // sessionStorage: open state survives in-tab navigation (registry redirect flow)
  // but does NOT persist across new browser sessions, so the chat is always closed
  // when a user opens a fresh tab or returns the next day.
  try { sessionStorage.setItem(OPEN_STATE_KEY, open ? '1' : '0'); } catch { /* ignore */ }
}
function loadOpenState() {
  try { return sessionStorage.getItem(OPEN_STATE_KEY) === '1'; } catch { return false; }
}

function newSessionId() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

/**
 * Returns the minimal telemetry fields for a single Ask Birch turn.
 *
 * The current Shopify Customer Privacy API decision is the sole consent
 * authority: we do not persist a separate preference. A visitor id is created
 * only after analytics consent and is deleted as soon as Shopify reports that
 * consent is withdrawn. If the API has not loaded yet, omit the fields and let
 * the next turn re-evaluate rather than assuming consent.
 */
function junoAnalyticsContext() {
  let analyticsAllowed;
  try {
    const privacy = /** @type {any} */ (window).Shopify?.customerPrivacy;
    analyticsAllowed = typeof privacy?.analyticsProcessingAllowed === 'function'
      ? privacy.analyticsProcessingAllowed()
      : undefined;
  } catch {
    return {};
  }

  if (analyticsAllowed !== true) {
    if (analyticsAllowed === false) {
      try { localStorage.removeItem(JUNO_VISITOR_KEY); } catch { /* ignore */ }
      // Tell the signed edge route that a known customer's permission was
      // withdrawn. No browser/customer identifier accompanies this flag.
      return { juno_analytics_allowed: false };
    }
    return {};
  }

  try {
    const existing = localStorage.getItem(JUNO_VISITOR_KEY);
    if (typeof existing === 'string' && existing.length > 0 && existing.length <= 128) {
      return { juno_analytics_allowed: true, juno_visitor_id: existing };
    }
    const visitorId = newSessionId();
    localStorage.setItem(JUNO_VISITOR_KEY, visitorId);
    return { juno_analytics_allowed: true, juno_visitor_id: visitorId };
  } catch {
    // Storage can be unavailable in private browsing. The current permitted
    // turn may still be measured with a one-turn, non-persistent identifier.
    return { juno_analytics_allowed: true, juno_visitor_id: newSessionId() };
  }
}

function loadOrCreateSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.id && Date.now() - (parsed.ts ?? 0) < SESSION_TTL_MS) {
        return { id: parsed.id, mode: parsed.mode ?? 'ai', pollSeq: parsed.pollSeq ?? 0 };
      }
    }
  } catch { /* ignore parse errors */ }
  return { id: newSessionId(), mode: 'ai', pollSeq: 0 };
}

/** @param {string} id @param {string} mode @param {number} pollSeq */
function persistSession(id, mode, pollSeq) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ id, mode, pollSeq, ts: Date.now() }));
  } catch { /* storage full or private mode */ }
}

/** Save the draft message log so a refresh mid-stream restores in-progress UI. */
/** @param {string} sessionId @param {Array<Record<string, unknown>>} entries */
function saveDraft(sessionId, entries) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ sessionId, entries, ts: Date.now() }));
  } catch { /* ignore */ }
}

/** @param {string} sessionId */
function loadDraft(sessionId) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (parsed.sessionId !== sessionId) return [];
    return Array.isArray(parsed.entries) ? parsed.entries : [];
  } catch { return []; }
}

const SESSIONS_KEY = 'birch_ai_sessions'; // device-local index of guest chats → powers the history page
const SESSIONS_MAX = 30;

/**
 * Upsert a chat into the device-local session index so the history page can
 * list every past guest chat on this browser (not just the active one — each
 * "new chat" mints a fresh id, and only the latest was ever recorded before).
 * @param {string} id @param {string} chip @param {number} count
 */
function recordSession(id, chip, count) {
  try {
    let list = [];
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (raw) { const p = JSON.parse(raw); if (Array.isArray(p)) list = p; }
    const ts = Date.now();
    const existing = list.find((s) => s && s.id === id);
    if (existing) {
      existing.ts = ts;
      if (chip) existing.chip = chip;
      if (typeof count === 'number') existing.count = count;
    } else {
      list.push({ id, chip: chip || '', count: count || 0, ts });
    }
    list.sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0)); // newest first
    if (list.length > SESSIONS_MAX) list = list.slice(0, SESSIONS_MAX);
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(list));
  } catch { /* storage full or private mode */ }
}

class BirchAICompanion extends HTMLElement {
  #session = loadOrCreateSession();
  get #sessionId() { return this.#session.id; }
  // Bumped every time the active conversation is swapped (new chat, or a past
  // chat loaded from the history page). Async work started under an older
  // generation — chiefly #rehydrateFromServer, which fires on mount and does
  // 2–3 sequential fetches — must NOT touch the DOM once this has moved on, or
  // the previous conversation lands on top of the fresh one and "New chat"
  // silently un-does itself.
  #sessionGen = 0;
  #open = false;
  // Scroll offset captured when the mobile lock engages, restored on close.
  #lockedScrollY = 0;
  // Desktop-only: expand the floating panel to fill the viewport. No-op on
  // mobile (panel is already full-screen) so the toggle is hidden there.
  #fullscreen = false;
  // True only while the CURRENT expansion was requested by a surface
  // (`birch:open-with-query { fullscreen: true }` — the registry builder
  // handoff), not chosen by the user with the header toggle. Only an
  // auto-expanded panel is allowed to auto-collapse when the task it was
  // expanded for finishes; a panel the user expanded stays expanded.
  #fullscreenAuto = false;
  /** @type {HTMLElement} */ #panel = /** @type {any} */ (undefined);
  /** @type {HTMLElement} */ #messageList = /** @type {any} */ (undefined);
  // The user bubble of the turn in flight. While set, scroll-to-end follows the
  // reply only until this bubble reaches the top of the list, so a long
  // response is read from its start instead of being dragged to the bottom.
  /** @type {HTMLElement | null} */ #responseAnchor = null;
  /** @type {HTMLInputElement} */ #input = /** @type {any} */ (undefined);
  /** @type {AbortController | null} */ #abortController = null;
  // Handoff state: 'ai' | 'waiting' | 'human'
  #mode = this.#session.mode;
  /** @type {EventSource | null} */ #eventSource = null;
  #lastSeq = this.#session.pollSeq;
  /** @type {HTMLElement | null} */ #handoffBanner = null;
  // Per-turn redirect tracking (#6 auto-redirect on first Birch card)
  #redirectedThisTurn = false;
  /** @type {string[]} */ #pendingBirchHandles = [];
  // handle → product title, populated from product cards so inline `[handle]`
  // citations in the assistant text can render as a titled link.
  /** @type {Map<string, string>} */ #titleByHandle = new Map();
  // Pending registry redirect — set on registry:build_complete, fired on
  // `done` so the post-build summary text streams in full before navigation.
  /** @type {string | null} */ #pendingRegistryEditId = null;
  // Sibling of the above for the no-navigation case: the build finished on the
  // registry-edit page we're ALREADY on, so there's nothing to redirect to —
  // instead the finished registry has to be uncovered. Set on
  // registry:build_complete, acted on at `done` so the summary text streams in
  // full first (same reasoning as the deferred redirect).
  /** @type {string | null} */ #pendingRegistryReveal = null;
  // Lightweight in-memory log mirrored to localStorage so refresh survives mid-stream.
  /** @type {Array<Record<string, unknown>>} */ #displayLog = [];
  // Slice 6 — selection from `birch:open-with-context`. Attached to the next
  // /chat request body and then cleared (per-turn hint, not sticky).
  /** @type {Array<Record<string, unknown>>} */ #pendingSelection = [];
  // Slice R-builder — `birch:open-with-query` may carry an intent string
  // (e.g. 'registry_builder') and an arbitrary metadata object (the registry-
  // create form fields). Both are attached to the next /chat request and
  // cleared after one send.
  /** @type {string} */ #pendingIntent = '';
  /** @type {'full_build'|'guided'|''} */ #pendingMode = '';
  /** @type {Record<string, unknown> | null} */ #pendingMetadata = null;
  // Per-turn registry reference from `birch:open-with-query` (detail.registryId).
  // Falls back to the registry-edit page's live getter at send time. Cleared after
  // one send so it never leaks onto an unrelated later turn.
  /** @type {string} */ #pendingRegistryId = '';
  // Per-turn "create a brand-new registry even if others exist" signal
  // (detail.startNew). The build buttons set it when no registry is in view.
  // Mutually exclusive with #pendingRegistryId; cleared after one send.
  /** @type {boolean} */ #pendingStartNew = false;
  // One-shot "the user tapped Search the web" flag. Set by the no-results
  // web-search button; attaches webSearch:true to the next send, then cleared.
  /** @type {boolean} */ #pendingWebSearch = false;
  // Per-turn "Find similar" click context (detail.findSimilar) — the clicked
  // card's structured identity ({ title, source, lushlifeProductId, handle,
  // category }) so the worker resolves the seed item deterministically instead
  // of re-deriving it from the message title (which misfired on external
  // LushLife items). Attached to the next /chat body, then cleared.
  /** @type {Record<string, unknown> | null} */ #pendingFindSimilar = null;
  // One-shot confirmation token from a cancel/return confirmation card. Sent
  // out-of-band in the next /chat body (confirmationToken) so the raw token
  // never appears in the visible chat message or the persisted transcript —
  // customers were seeing "…with token eyJ…" as their own bubble. Cleared
  // after the send like the other per-turn hints.
  /** @type {string} */ #pendingConfirmation = '';
  // Sticky for the session once a registry build is launched (CTA intent) or
  // any registry:* event is seen. The worker keeps the registry-builder skill
  // active across turns even without re-sending intent, so the theme mirrors
  // that here to know when to bridge the "AI replied → build tool runs" gap
  // with a loader on follow-up turns (e.g. the persona-confirm turn).
  /** @type {boolean} */ #registryBuildActive = false;
  // Set to the in-progress registryId when a build is streaming. Cleared on
  // build_complete, build_failed, abort, or session reset. Used to toggle the
  // send button to a stop icon and route the form submit to #abortBuild().
  /** @type {string | null} */ #buildingRegistryId = null;
  // Calm build experience: the personalized line script + the timers that
  // cross-fade through it. Cleared on build_complete/failed and any session
  // reset so a stale timer never ticks against a torn-down panel.
  /** @type {string[]} */ #buildScript = [];
  /** @type {number} */ #buildScriptIdx = 0;
  /** @type {ReturnType<typeof setInterval> | null} */ #buildLineTimer = null;
  /** @type {ReturnType<typeof setInterval> | null} */ #buildSubTimer = null;
  // Claude-Code-style message queue: when a stream is in flight, further
  // sends from the user buffer here. After the current SSE stream's `done`
  // event drains, we dequeue and fire the next turn. Lets the user say
  // "actually also add a stroller" while the AI is mid-build instead of
  // losing the message or having to wait silently.
  /** @type {boolean} */ #sendInFlight = false;
  /** @type {string[]} */ #queuedMessages = [];
  #voiceMode = false;
  #voiceState = 'idle';
  #voiceTranscript = '';
  #voiceError = '';
  /** @type {HTMLElement | null} */ #voicePanel = /** @type {any} */ (undefined);
  /** @type {HTMLElement | null} */ #voiceStatus = /** @type {any} */ (undefined);
  /** @type {HTMLElement | null} */ #voiceTranscriptDisplay = /** @type {any} */ (undefined);
  /** @type {HTMLButtonElement | null} */ #voiceToggle = /** @type {any} */ (undefined);
  /** @type {any} */ #voiceRecognizer = null;

  connectedCallback() {
    this.#render();
    this.#attachEvents();
    this.#wireGlobalListeners();
    this.#syncJunoConsentWithdrawal();
    this.#startJunoProductDwell();
    // Shopify emits this whenever its native consent UI is saved. A withdrawal
    // removes the edge-side order-attribution permission immediately, even if
    // the shopper never sends another Ask Birch message.
    document.addEventListener('visitorConsentCollected', () => this.#syncJunoConsentWithdrawal());
    // Resume SSE stream if we were in waiting/human mode before refresh
    if (this.#mode === 'waiting' || this.#mode === 'human') {
      this.#enterHandoffMode(this.#mode);
    }
    // Restore in-progress draft entries first (zero-cost, no network)…
    const draft = loadDraft(this.#sessionId);
    if (draft.length > 0) {
      this.#displayLog = draft;
      this.#replayDisplayLog();
    }
    // …then fetch the durable D1 history in the background and merge.
    void this.#rehydrateFromServer();
    // Restore open state. If the user was chatting before navigating
    // (e.g. registry build flow auto-redirected post-build), reopen the
    // panel so the conversation continues visibly instead of getting
    // hidden behind the FAB. Exception: a mobile "Open full page" tap tags the
    // URL with chat=min so the panel stays minimized here instead of covering
    // the results (see #renderShowResultsBox); honor that over the saved state.
    if (new URLSearchParams(location.search).get('chat') === 'min') {
      this.#toggle(false);
    } else if (loadOpenState()) {
      this.#toggle(true);
    }
    // Show the contextual starter pills if (and only if) the conversation
    // is empty. Runs synchronously here so the hints are present the moment
    // the panel opens; the async rehydrate above wipes them if it finds a
    // durable transcript (it clears #messageList then rebuilds + re-syncs).
    this.#syncEmptyState();
  }

  #render() {
    this.innerHTML = `
      <div class="birch-backdrop" aria-hidden="true"></div>
      <div class="birch-panel" role="dialog" aria-modal="true" aria-label="Birch AI Companion" hidden>
        <div class="birch-panel__header">
          <button type="button" class="birch-panel__back" aria-label="Minimize chat">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <span class="birch-panel__title">Ask Birch</span>
          <div class="birch-panel__actions">
            <a class="birch-icon-btn birch-icon-btn--history" href="/pages/ai-chat-history" aria-label="Past chats" data-tooltip="Past chats">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6">
                <circle cx="10" cy="10" r="7"/>
                <path d="M10 5v5l3 2" stroke-linecap="round"/>
              </svg>
            </a>
            <button type="button" class="birch-icon-btn birch-icon-btn--reset" aria-label="New chat" data-tooltip="New chat">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9 3.75H5.25A1.75 1.75 0 0 0 3.5 5.5v9.25A1.75 1.75 0 0 0 5.25 16.5h9.25a1.75 1.75 0 0 0 1.75-1.75V11"/>
                <path d="M14.6 3.15a1.485 1.485 0 0 1 2.1 2.1L10.1 11.85l-2.85.75.75-2.85 6.6-6.6z"/>
              </svg>
            </button>
            <button type="button" class="birch-icon-btn birch-icon-btn--fullscreen" aria-label="Full screen" aria-pressed="false" data-tooltip="Full screen">
              <svg class="birch-fs-icon birch-fs-icon--enter" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M8 3H5a2 2 0 0 0-2 2v3"/>
                <path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
                <path d="M3 16v3a2 2 0 0 0 2 2h3"/>
                <path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
              </svg>
              <svg class="birch-fs-icon birch-fs-icon--exit" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M8 3v3a2 2 0 0 1-2 2H3"/>
                <path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
                <path d="M3 16h3a2 2 0 0 1 2 2v3"/>
                <path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
              </svg>
            </button>
            <button class="birch-panel__close" aria-label="Close">&#x2715;</button>
          </div>
        </div>
        <div class="birch-messages" role="log" aria-live="polite"></div>
        <form class="birch-input-row">
          <input
            class="birch-input"
            type="text"
            placeholder="Ask about products, certifications, ingredients&hellip;"
            autocomplete="off"
            maxlength="500"
          >
          <button type="button" class="birch-input-voice birch-voice" aria-label="Activate voice mode" title="Activate voice mode">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="2" width="6" height="11" rx="3"/>
              <path d="M5 10a7 7 0 0014 0"/>
              <line x1="12" y1="19" x2="12" y2="22"/>
              <line x1="8" y1="22" x2="16" y2="22"/>
            </svg>
            <span class="birch-input-voice__dot"></span>
          </button>
          <button type="submit" class="birch-send" aria-label="Send">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2c.4 3.6 1.4 5.6 3 7.2 1.6 1.6 3.6 2.4 7 2.8-3.4.4-5.4 1.2-7 2.8-1.6 1.6-2.6 3.6-3 7.2-.4-3.6-1.4-5.6-3-7.2-1.6-1.6-3.6-2.4-7-2.8 3.4-.4 5.4-1.2 7-2.8 1.6-1.6 2.6-3.6 3-7.2Z" fill="currentColor"/>
              <path d="M19 3c.2 1.4.5 2 1.1 2.6.6.6 1.2.9 2.6 1.1-1.4.2-2 .5-2.6 1.1-.6.6-.9 1.2-1.1 2.6-.2-1.4-.5-2-1.1-2.6-.6-.6-1.2-.9-2.6-1.1 1.4-.2 2-.5 2.6-1.1.6-.6.9-1.2 1.1-2.6Z" fill="currentColor" opacity=".85"/>
            </svg>
          </button>
        </form>
        <div class="birch-voice-panel" hidden>
          <div class="birch-voice-panel__inner">
            <div class="birch-voice-orb"></div>
            <div class="birch-voice-copy">
              <div class="birch-voice-status">Listening…</div>
              <div class="birch-voice-transcript" aria-live="polite"></div>
            </div>
          </div>
          <div class="birch-voice-actions">
            <button type="button" class="birch-voice-close" aria-label="Back to keyboard">Back to keyboard</button>
          </div>
        </div>
      </div>
    `;

    this.#panel = /** @type {HTMLElement} */ (this.querySelector('.birch-panel'));
    this.#messageList = /** @type {HTMLElement} */ (this.querySelector('.birch-messages'));
    this.#input = /** @type {HTMLInputElement} */ (this.querySelector('.birch-input'));
    this.#voicePanel = /** @type {HTMLElement | null} */ (this.querySelector('.birch-voice-panel'));
    this.#voiceStatus = /** @type {HTMLElement | null} */ (this.querySelector('.birch-voice-status'));
    this.#voiceTranscriptDisplay = /** @type {HTMLElement | null} */ (this.querySelector('.birch-voice-transcript'));
    this.#voiceToggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('.birch-input-voice'));
  }

  #attachEvents() {
    const externalTrigger = document.querySelector('[data-ai-trigger]');
    if (externalTrigger) externalTrigger.addEventListener('click', () => this.#toggle());
    /** @type {HTMLElement} */ (this.querySelector('.birch-panel__close')).addEventListener('click', () => this.#toggle(false));
    // Clicking the top bar itself (title / empty space) also hides the panel —
    // but not when the click lands on an action control (history / new chat /
    // close), which have their own behaviour.
    const header = this.querySelector('.birch-panel__header');
    if (header) {
      header.addEventListener('click', (/** @type {Event} */ e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target.closest('.birch-panel__actions')) return;
        this.#toggle(false);
      });
    }
    const backBtn = this.querySelector('.birch-panel__back');
    if (backBtn) backBtn.addEventListener('click', () => this.#toggle(false));
    const fullscreenBtn = this.querySelector('.birch-icon-btn--fullscreen');
    if (fullscreenBtn) fullscreenBtn.addEventListener('click', () => {
      // The user is taking control of the panel size — from here on this
      // expansion is theirs, so nothing may collapse it on their behalf.
      this.#fullscreenAuto = false;
      this.#toggleFullscreen();
    });
    const backdrop = this.querySelector('.birch-backdrop');
    if (backdrop) backdrop.addEventListener('click', () => this.#toggle(false));
    /** @type {HTMLFormElement} */ (this.querySelector('form')).addEventListener('submit', (e) => {
      e.preventDefault();
      if (this.#buildingRegistryId && !this.#input.value.trim()) {
        void this.#abortBuild();
      } else {
        this.#send();
      }
    });
    this.#input.addEventListener('input', () => this.#updateSendMode());
    /** @type {HTMLElement} */ (this.querySelector('.birch-icon-btn--reset')).addEventListener('click', () => this.#resetChat());
    // History icon is a plain link to /pages/ai-chat-history. On MOBILE the panel
    // is full-screen and its open state is persisted, so it would re-open over
    // the history page — minimize it before the link navigates so the page isn't
    // hidden behind the chat. (Desktop: the panel is a small floating box, so
    // leave it open.) The link's own navigation still proceeds.
    const historyBtn = this.querySelector('.birch-icon-btn--history');
    if (historyBtn) {
      historyBtn.addEventListener('click', () => {
        if (window.matchMedia('(max-width: 768px)').matches) this.#toggle(false);
      });
    }
    if (this.#voiceToggle) {
      this.#voiceToggle.addEventListener('click', () => {
        if (this.#voiceMode) {
          this.#stopVoiceCapture();
        } else {
          void this.#startVoiceCapture();
        }
      });
    }
    const voiceClose = /** @type {HTMLElement | null} */ (this.querySelector('.birch-voice-close'));
    if (voiceClose) voiceClose.addEventListener('click', () => this.#stopVoiceCapture());

    // Delegated: any in-chat link that navigates away (collection / page /
    // product cards, the "Open full page" box, markdown links) must minimize the
    // full-screen panel first on MOBILE, or it reopens covering the destination.
    // Desktop is left untouched (links navigate, chat stays open). New-tab,
    // download, hash-only, and external/mailto/tel links are passed through.
    this.#messageList.addEventListener('click', (e) => {
      if (!window.matchMedia('(max-width: 768px)').matches) return;
      const a = /** @type {HTMLAnchorElement | null} */ (
        /** @type {Element} */ (e.target)?.closest?.('a[href]')
      );
      if (!a || !this.#messageList.contains(a)) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      const href = a.getAttribute('href') || '';
      if (!href || href.startsWith('#')) return;
      let u;
      try { u = new URL(a.href, location.href); } catch { return; }
      if (u.origin !== location.origin) return; // external / mailto / tel
      e.preventDefault();
      this.#navigateMinimizingOnMobile(u.toString());
    });
  }

  #wireGlobalListeners() {
    // Nav search bar (and any other surface) dispatches this when it wants to
    // hand off a query to the chat. `fresh` forces a new session id so the
    // search bar acts as a starting point for a brand-new conversation.
    window.addEventListener('birch:open-with-query', (/** @type {any} */ ev) => {
      const detail = ev.detail || {};
      const query = String(detail.query ?? '').trim();
      if (detail.fresh) this.#startFreshSession();
      // Capture intent + metadata for the next /chat send. Cleared after
      // one send (per-turn hint, not sticky beyond the body field — the
      // worker persists the intent on the session DO if needed).
      if (typeof detail.intent === 'string' && detail.intent.length > 0) {
        this.#pendingIntent = detail.intent;
      }
      // Phase 10: registry build mode ("full_build" | "guided") from the
      // Surprise Me / Walk Me Through buttons. Rides the next /chat body.
      if (detail.mode === 'guided' || detail.mode === 'full_build') {
        this.#pendingMode = detail.mode;
      }
      if (detail.metadata && typeof detail.metadata === 'object') {
        this.#pendingMetadata = /** @type {Record<string, unknown>} */ (detail.metadata);
      }
      // Structured identity of the card behind a "Find similar" click. Rides
      // the next /chat body as `findSimilar` (per-turn, cleared after send).
      if (detail.findSimilar && typeof detail.findSimilar === 'object') {
        this.#pendingFindSimilar = /** @type {Record<string, unknown>} */ (detail.findSimilar);
      }
      // Explicit registry reference for this turn (e.g. find-similar / finish-
      // building dispatched from the registry-edit page). Overrides the page
      // getter for the next send.
      if (typeof detail.registryId === 'string' && detail.registryId.length > 0) {
        this.#pendingRegistryId = detail.registryId;
      }
      // Explicit "start a new registry" signal (build buttons, no registry in
      // view). Wins over any registry reference for this turn.
      if (detail.startNew === true) {
        this.#pendingStartNew = true;
      }
      this.#toggle(true);
      // A surface can ask for the expanded panel when the chat IS the task
      // rather than a side conversation — the registry intake's "Smart Registry
      // Builder" hands off here and wants the whole viewport for the build.
      // Only ever expands: a request never collapses a panel the user chose to
      // expand. No-op on mobile, where the panel is already full-screen and the
      // stylesheet's !important geometry ignores the class.
      if (detail.fullscreen === true && !this.#fullscreen) {
        this.#toggleFullscreen(true);
        this.#fullscreenAuto = true;
      }
      if (query) {
        this.#input.value = query;
        // Allow open animation to settle before submission
        requestAnimationFrame(() => this.#send());
      }
    });

    // Slice 6 — surfaces with selection (collection compare button, wishlist
    // compare, PDP "compare with similar", in-chat "+" → action bar) dispatch
    // this. The selection rides on the next #send() request body.
    // Per refined §3 continuity rule: continue the current chat by default;
    // caller passes `fresh: true` only for surfaces that should start fresh
    // (nav search box, explicit reset).
    window.addEventListener('birch:open-with-context', (/** @type {any} */ ev) => {
      const detail = ev.detail || {};
      const query = String(detail.query ?? '').trim();
      const incomingSelection = Array.isArray(detail.selection) ? detail.selection : [];

      if (detail.fresh === true) this.#startFreshSession();

      // Stash the selection for the next #send() call. We don't piggyback on
      // every send forever — this is a per-turn hint, then cleared.
      this.#pendingSelection = incomingSelection.slice(0, 4);

      // Visual marker so the user sees what just got attached (refined §3
      // recommendation: "Added N from <source>").
      if (this.#pendingSelection.length > 0) {
        this.#renderContextChip(this.#pendingSelection.length, detail.source || 'selection');
      }

      this.#toggle(true);
      if (query) {
        this.#input.value = query;
        requestAnimationFrame(() => this.#send());
      }
    });

    // Chat-history page (`/pages/ai-chat-history`) dispatches this when a past
    // chat row is clicked. We load that session straight into the floating box
    // — no navigation — so the conversation reopens inline instead of bouncing
    // to the AI search page (which only ever showed product cards, never the
    // transcript, leaving the chat box out of sync).
    window.addEventListener('birch:load-session', (/** @type {any} */ ev) => {
      const detail = ev.detail || {};
      const sessionId = String(detail.sessionId ?? '').trim();
      if (sessionId) this.#loadSession(sessionId);
    });

    // Android back-gesture / browser back button — close the chat if open on mobile.
    window.addEventListener('popstate', () => {
      if (!this.#panel.hidden && window.matchMedia('(max-width: 768px)').matches) {
        this.#toggle(false);
      }
    });

    // Keep the panel anchored to the visual viewport on mobile.
    // On Chrome/Safari iOS the layout viewport does NOT shrink when the
    // keyboard opens, so a position:fixed panel drifts and the header
    // scrolls off screen. visualViewport gives us the exact visible rect
    // and we pin the panel to it on every resize (keyboard open/close).
    if (window.visualViewport) {
      const syncPanel = () => {
        if (this.#panel.hidden) return;
        if (!window.matchMedia('(max-width: 768px)').matches) return;
        const vv = window.visualViewport;
        if (!vv) return;
        // Use setProperty(...,'important') so these values override the
        // !important declarations in the stylesheet (inline styles alone
        // do not beat !important CSS rules).
        this.#panel.style.setProperty('top',    `${vv.offsetTop}px`, 'important');
        this.#panel.style.setProperty('height', `${vv.height}px`,    'important');
      };
      window.visualViewport.addEventListener('resize', syncPanel);
      window.visualViewport.addEventListener('scroll', syncPanel);
    }
  }

  /** @param {number} count @param {string} source */
  #renderContextChip(count, source) {
    const chip = document.createElement('div');
    chip.className = 'birch-context-chip';
    chip.textContent = `✨ Added ${count} from ${source}`;
    this.#messageList.appendChild(chip);
    this.#scrollMessagesToEnd();
  }

  // ── Empty-state starter pills ─────────────────────────────────────────────
  // When the conversation is empty we hint at what the assistant can do on the
  // current page with 3–5 clickable example prompts. They're derived fresh from
  // window.BirchAI.context on every load (the storefront does full page loads,
  // so the page context — and therefore the pills — is current per page). The
  // pills are NOT part of #displayLog; they're ephemeral UI that disappears the
  // moment any message lands (see #appendMessage → #clearEmptyState).

  /**
   * Reconcile the starter pills with the current conversation state: show them
   * only when nothing has loaded into the chat, remove them otherwise.
   */
  #syncEmptyState() {
    // Whatever we decide below is the settled state, so the transcript skeleton
    // has done its job — a past chat either landed or turned out to be empty.
    this.#clearTranscriptLoading();
    const hasContent = this.#displayLog.length > 0
      || !!this.#messageList.querySelector('.birch-msg, .birch-receipt, .birch-context-chip');
    if (hasContent) this.#clearEmptyState();
    else this.#renderEmptyState();
  }

  /**
   * Skeleton shown while a past chat's transcript is being pulled from D1.
   * Opening a chat from the history page used to flash the contextual starter
   * pills — the "start a new chat" surface — for as long as the fetch took,
   * which read as "this chat is empty" right before the real messages replaced
   * it. Show placeholder bubbles instead: same shape as the transcript that is
   * about to land, so nothing contradicts itself mid-load.
   */
  #renderTranscriptLoading() {
    this.#clearEmptyState();
    if (this.#messageList.querySelector('.birch-transcript-loading')) return;
    const wrap = document.createElement('div');
    wrap.className = 'birch-transcript-loading';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-label', 'Loading your chat');
    // user / assistant / assistant, so the skeleton mirrors a real exchange.
    for (const variant of ['user', 'assistant', 'assistant']) {
      const bar = document.createElement('div');
      bar.className = `birch-transcript-loading__bar birch-transcript-loading__bar--${variant}`;
      wrap.appendChild(bar);
    }
    this.#messageList.appendChild(wrap);
  }

  #clearTranscriptLoading() {
    const el = this.#messageList.querySelector('.birch-transcript-loading');
    if (el) el.remove();
  }

  #clearEmptyState() {
    const el = this.#messageList.querySelector('.birch-empty-state');
    if (el) el.remove();
  }

  /** Build and append the contextual starter pills (no-op if already present). */
  #renderEmptyState() {
    if (this.#messageList.querySelector('.birch-empty-state')) return;
    const { intro, pills } = this.#emptyStateSuggestions();
    if (!pills.length) return;

    const wrap = document.createElement('div');
    wrap.className = 'birch-empty-state';

    if (intro) {
      const p = document.createElement('p');
      p.className = 'birch-empty-state__intro';
      p.textContent = intro;
      wrap.appendChild(p);
    }

    const row = document.createElement('div');
    row.className = 'birch-empty-state__pills';
    for (const s of pills.slice(0, 5)) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'birch-pill birch-empty-state__pill';
      btn.textContent = s.label;
      btn.addEventListener('click', () => {
        // Reuse the normal send path so a tapped pill behaves exactly like the
        // user typing the prompt — same orchestration, same page context.
        this.#input.value = s.query || s.label;
        this.#send();
      });
      row.appendChild(btn);
    }
    wrap.appendChild(row);
    this.#messageList.appendChild(wrap);
  }

  /**
   * Pick the starter prompts for the current page. Delegates to the shared,
   * DOM-free module (assets/birch-ai-suggestions.js) so the widget and the
   * test harness stay in lockstep.
   * @returns {{ intro: string, pills: Array<{ label: string, query: string }> }}
   */
  #emptyStateSuggestions() {
    const ctx = /** @type {any} */ ((/** @type {any} */ (window).BirchAI?.context) ?? {});
    return emptyStateSuggestions(ctx);
  }

  /**
   * Drop every per-turn hint and queued message so a swapped-in conversation
   * starts clean. Callers invoke this BEFORE attaching any new hints (the
   * `birch:open-with-query` / `-context` handlers reset first, then assign).
   */
  #clearPerTurnState() {
    this.#queuedMessages = [];
    this.#pendingSelection = [];
    this.#pendingIntent = '';
    this.#pendingMode = '';
    this.#pendingMetadata = null;
    this.#pendingRegistryId = '';
    this.#pendingStartNew = false;
    this.#pendingWebSearch = false;
    this.#pendingFindSimilar = null;
    this.#pendingConfirmation = '';
    this.#pendingRegistryEditId = null;
    this.#pendingRegistryReveal = null;
    this.#pendingBirchHandles = [];
    this.#redirectedThisTurn = false;
    this.#titleByHandle.clear();
  }

  #startFreshSession() {
    // Invalidate anything already in flight for the outgoing conversation
    // BEFORE the swap, so a late rehydrate/stream can't repaint over the new one.
    this.#sessionGen++;
    this.#abortController?.abort();
    this.#stopStream();
    const id = newSessionId();
    this.#session = { id, mode: 'ai', pollSeq: 0 };
    this.#mode = 'ai';
    this.#lastSeq = 0;
    this.#displayLog = [];
    this.#registryBuildActive = false;
    this.#buildingRegistryId = null;
    this.#clearPerTurnState();
    this.#stopBuildCalm();
    this.#updateSendMode();
    saveDraft(id, []);
    persistSession(id, 'ai', 0);
    this.#messageList.innerHTML = '';
    if (this.#handoffBanner) { this.#handoffBanner.remove(); this.#handoffBanner = null; }
    // Fresh, empty session → surface the contextual starter pills again.
    this.#syncEmptyState();
  }

  #resetChat() {
    this.#startFreshSession();
  }

  /**
   * Adopt an existing session and reopen it inline in the floating box.
   * Used by the chat-history page so clicking a past chat reloads its
   * transcript here instead of redirecting to the AI search page.
   * @param {string} sessionId
   */
  #loadSession(sessionId) {
    // Already the active session — just surface the panel (and pull history
    // if the transcript was never populated, e.g. fresh page load).
    if (sessionId === this.#sessionId) {
      this.#toggle(true);
      if (this.#displayLog.length === 0) {
        this.#renderTranscriptLoading();
        void this.#rehydrateFromServer();
      }
      return;
    }
    // Tear down any in-flight stream / handoff / rehydrate from the previous
    // session (the generation bump makes a late rehydrate a no-op).
    this.#sessionGen++;
    this.#abortController?.abort();
    this.#stopStream();
    // Make the requested session active and persist it to the same
    // localStorage slot the widget reads on mount, so a refresh keeps it.
    this.#session = { id: sessionId, mode: 'ai', pollSeq: 0 };
    this.#mode = 'ai';
    this.#lastSeq = 0;
    this.#displayLog = [];
    this.#registryBuildActive = false;
    this.#buildingRegistryId = null;
    this.#clearPerTurnState();
    this.#stopBuildCalm();
    this.#updateSendMode();
    persistSession(sessionId, 'ai', 0);
    saveDraft(sessionId, []);
    // Reset the visible transcript, then load the durable history from D1.
    this.#messageList.innerHTML = '';
    if (this.#handoffBanner) { this.#handoffBanner.remove(); this.#handoffBanner = null; }
    this.#updatePlaceholder();
    this.#toggle(true);
    // Skeleton first — NOT the starter pills. The panel opens instantly while
    // the transcript is still in flight, and the pills are the empty-chat
    // surface, so showing them here told the user "no messages" a beat before
    // the messages appeared. #syncEmptyState (end of the rehydrate) swaps the
    // skeleton for the transcript, or for the pills if the chat really is empty.
    this.#renderTranscriptLoading();
    void this.#rehydrateFromServer();
  }

  /** Reset the input placeholder to the neutral default. Used when leaving handoff. */
  #updatePlaceholder() {
    this.#input.placeholder = 'Ask about products, orders, anything…';
  }

  /** @param {boolean} [force] */
  #toggle(force) {
    this.#open = force !== undefined ? force : !this.#open;
    this.#panel.hidden = !this.#open;
    const fab = document.querySelector('[data-ai-trigger]');
    if (fab) fab.setAttribute('aria-expanded', String(this.#open));
    const backdrop = this.querySelector('.birch-backdrop');
    if (backdrop) backdrop.classList.toggle('birch-backdrop--visible', this.#open);
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    document.body.classList.toggle('birch-chat-open', this.#open);
    // Prevent page scroll behind the panel on mobile. iOS Safari ignores
    // `overflow: hidden` on <body> for touch scrolling, so we pin the body
    // with `position: fixed` and offset it by the current scroll position,
    // restoring that position on close. `.birch-chat-lock` carries the fixed
    // styles; the inline top offset preserves where the user was.
    if (isMobile) {
      if (this.#open) {
        this.#lockedScrollY = window.scrollY;
        document.body.style.top = `-${this.#lockedScrollY}px`;
        document.body.classList.add('birch-chat-lock');
      } else {
        document.body.classList.remove('birch-chat-lock');
        document.body.style.removeProperty('top');
        // behavior:'instant' overrides the theme's `html { scroll-behavior:
        // smooth }` (base.css) — otherwise restoring the offset animates as a
        // visible page scroll when the chat closes.
        window.scrollTo({ top: this.#lockedScrollY, left: 0, behavior: 'instant' });
      }
    }
    // Reset visualViewport-pinned inline styles when closing so desktop
    // panel is never affected by leftover top/height values.
    if (!this.#open) {
      this.#panel.style.removeProperty('top');
      this.#panel.style.removeProperty('height');
    }
    // Push history entry on mobile open so the back gesture closes the chat.
    if (this.#open && isMobile) {
      history.pushState({ birchChat: true }, '');
    }
    if (this.#open && !window.matchMedia('(max-width: 768px)').matches) {
      // Only auto-focus on desktop — on mobile this triggers the keyboard
      // immediately, which pushes the panel up before the user is ready.
      this.#input.focus();
    } else if (this.#voiceMode) {
      void this.#stopVoiceCapture();
    }
    persistOpenState(this.#open);
  }

  /**
   * Navigate to an internal URL, minimizing the full-screen chat first on
   * MOBILE so it doesn't reopen over the destination — the panel's open state
   * is persisted + rehydrated on mount, and /search?view=ai&session pages also
   * auto-open the chat (see the DOMContentLoaded handler), so closing here +
   * tagging chat=min is what keeps it minimized. DESKTOP: the panel is a small
   * floating box, so leave it open and just navigate. Used by every in-chat
   * link (delegated in #attachEvents) and programmatic redirects (e.g. tracker).
   * @param {string} url
   */
  #navigateMinimizingOnMobile(url) {
    if (!window.matchMedia('(max-width: 768px)').matches) {
      window.location.assign(url);
      return;
    }
    this.#toggle(false);
    let dest = url;
    try {
      const u = new URL(url, location.href);
      if (u.searchParams.get('view') === 'ai' && u.searchParams.get('session')) {
        u.searchParams.set('chat', 'min');
      }
      dest = u.toString();
    } catch { /* fall back to the raw url */ }
    window.location.assign(dest);
  }

  /**
   * Desktop-only expand/restore of the floating panel. Toggles a class the CSS
   * uses to grow the panel toward the viewport edges; on mobile the panel is
   * already full-screen so the button is hidden and this is effectively unused.
   * @param {boolean} [force]
   */
  #toggleFullscreen(force) {
    this.#fullscreen = force !== undefined ? force : !this.#fullscreen;
    this.#panel.classList.toggle('birch-panel--fullscreen', this.#fullscreen);
    const btn = this.querySelector('.birch-icon-btn--fullscreen');
    if (btn) {
      btn.setAttribute('aria-pressed', String(this.#fullscreen));
      const label = this.#fullscreen ? 'Exit full screen' : 'Full screen';
      btn.setAttribute('aria-label', label);
      btn.setAttribute('data-tooltip', label);
    }
    if (!window.matchMedia('(max-width: 768px)').matches) this.#input.focus();
  }

  #renderVoiceState() {
    if (!this.#voicePanel || !this.#voiceStatus || !this.#voiceTranscriptDisplay || !this.#voiceToggle) return;
    this.#voicePanel.hidden = !this.#voiceMode;
    this.#voiceToggle.setAttribute('aria-pressed', String(this.#voiceMode));
    this.#voiceToggle.classList.toggle('birch-voice--listening', this.#voiceMode && this.#voiceState === 'listening');

    if (this.#voiceMode) {
      if (this.#voiceError) {
        this.#voiceStatus.textContent = this.#voiceError;
      } else if (this.#voiceState === 'processing') {
        this.#voiceStatus.textContent = 'Processing your voice input…';
      } else if (this.#voiceState === 'speaking') {
        this.#voiceStatus.textContent = 'Speaking Birch AI response…';
      } else {
        this.#voiceStatus.textContent = 'Listening…';
      }
    } else {
      this.#voiceStatus.textContent = 'Voice mode ready. Tap the mic to listen.';
    }

    this.#voiceTranscriptDisplay.textContent = this.#voiceTranscript || 'Speak naturally. Birch will reply when you stop.';
  }

  async #startVoiceCapture() {
    if (this.#voiceMode) return;
    this.#voiceMode = true;
    this.#voiceState = 'starting';
    this.#voiceError = '';
    this.#voiceTranscript = '';
    this.#renderVoiceState();
    this.#toggle(true);

    if (!BirchSpeechRecognitionCtor) {
      this.#voiceError = 'Voice recognition is not supported in this browser.';
      this.#voiceState = 'error';
      this.#renderVoiceState();
      this.#voiceMode = false;
      return;
    }

    try {
      const recognition = new BirchSpeechRecognitionCtor();
      recognition.lang = 'en-US';
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;

      recognition.onstart = () => {
        this.#voiceState = 'listening';
        this.#voiceError = '';
        this.#renderVoiceState();
      };

      /** @param {any} event */
      recognition.onresult = (event) => {
        const results = Array.from(/** @type {any[]} */ (event.results))
          .map((result) => result[0]?.transcript ?? '')
          .join('');
        this.#voiceTranscript = results;
        this.#renderVoiceState();

        const lastResult = event.results[event.results.length - 1];
        if (lastResult?.isFinal) {
          this.#input.value = results.trim();
          this.#voiceState = 'processing';
          this.#renderVoiceState();
          void this.#send();
        }
      };

      /** @param {any} event */
      recognition.onerror = (event) => {
        this.#voiceError = event.error === 'no-speech'
          ? 'No speech detected. Please try again.'
          : 'Voice recognition error. Please try again.';
        this.#voiceState = 'error';
        this.#renderVoiceState();
        this.#stopVoiceCapture();
      };

      recognition.onend = () => {
        if (!this.#voiceTranscript && this.#voiceMode) {
          this.#voiceError = 'No speech detected. Please try again.';
          this.#voiceState = 'error';
          this.#renderVoiceState();
        }
      };

      recognition.start();
      this.#voiceRecognizer = recognition;
    } catch (err) {
      console.error('[BirchAICompanion] voice error', err);
      this.#voiceError = 'Unable to start voice recognition.';
      this.#voiceState = 'error';
      this.#renderVoiceState();
      this.#voiceMode = false;
    }
  }

  async #stopVoiceCapture() {
    if (!this.#voiceMode && !this.#voiceRecognizer) return;
    this.#voiceMode = false;
    this.#voiceState = 'idle';
    this.#voiceError = '';
    this.#renderVoiceState();

    if (this.#voiceRecognizer) {
      try {
        this.#voiceRecognizer.onresult = null;
        this.#voiceRecognizer.onend = null;
        this.#voiceRecognizer.onerror = null;
        this.#voiceRecognizer.onstart = null;
        this.#voiceRecognizer.stop();
      } catch (e) {
        console.warn(e);
      }
      this.#voiceRecognizer = null;
    }

    if (this.#voicePanel) this.#voicePanel.hidden = true;
  }

  /** @param {string} text */
  async #speakText(text) {
    if (!this.#voiceMode || !text || !text.trim()) return;
    this.#voiceState = 'speaking';
    this.#renderVoiceState();

    const speakBrowserText = async () => {
      if (!SpeechSynthesisConstructor) throw new Error('Browser speech synthesis unavailable');
      return new Promise((resolve, reject) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onend = resolve;
        utterance.onerror = (event) => {
          reject(new Error(event?.error || 'Speech synthesis playback failed'));
        };
        SpeechSynthesisConstructor.cancel();
        SpeechSynthesisConstructor.speak(utterance);
      });
    };

    try {
      const resp = await fetch(`${PROXY}/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'EXAVITQu4vr4xnSDxMaL' }),
      });
      if (!resp.ok) throw new Error(`TTS request failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (this.#voiceMode) {
          this.#voiceState = 'listening';
          this.#renderVoiceState();
        }
      };
      await audio.play();
    } catch (err) {
      if (SpeechSynthesisConstructor) {
        try {
          await speakBrowserText();
          if (this.#voiceMode) {
            this.#voiceState = 'listening';
            this.#renderVoiceState();
          }
          return;
        } catch (fallbackErr) {
          console.warn('[BirchAICompanion] browser speech synthesis fallback failed', fallbackErr);
        }
      }
      console.error('[BirchAICompanion] voice TTS failed', err);
      this.#voiceError = 'Voice playback failed.';
      this.#voiceState = 'error';
      this.#renderVoiceState();
    }
  }

  // ── Rehydrate from D1 ────────────────────────────────────────────────────
  // Pulls the durable per-message log and replays it as text + cards. Both
  // Birch (by handle) and LushLife (by id) products are bulk-fetched and
  // rendered inline; Birch results also get an "Open full page" box.
  async #rehydrateFromServer() {
    // Pin the conversation this replay belongs to. Every await below is a
    // window in which the user can hit "New chat", type in the search box
    // (which starts a fresh chat), or open a past chat from the history page —
    // all of which bump #sessionGen. Rehydrating past that point would wipe the
    // new conversation and repaint the old one, which is what made "New chat"
    // look like it silently did nothing.
    const gen = this.#sessionGen;
    const sessionId = this.#sessionId;
    const stale = () => this.#sessionGen !== gen;
    try {
      const resp = await fetch(`${PROXY}/sessions/${encodeURIComponent(sessionId)}`);
      if (stale() || !resp.ok) return;
      const data = await resp.json();
      if (stale()) return;
      const messages = Array.isArray(data.messages) ? data.messages : [];
      if (messages.length === 0) return;

      // Freshness gate: if the local draft has MORE user turns than the
      // server transcript, the server is behind us — almost certainly a
      // navigation-aborted SSE stream (e.g. build_complete → redirect to
      // /pages/registry-edit fires before D1 flushes the final assistant
      // message). Clobbering with the stale server snapshot would drop the
      // assistant's summary text that the local draft already captured.
      // Keep the local replay instead; D1 will catch up on next page load.
      const localUserCount = this.#displayLog.filter((e) => e.kind === 'user').length;
      const serverUserCount = messages.filter((/** @type {Record<string, unknown>} */ m) => m.role === 'user').length;
      if (localUserCount > serverUserCount) return;

      // A turn is streaming right now. The rebuild below clears #messageList,
      // which would detach the live assistant bubble (deltas keep landing on an
      // orphaned node) and drop the user's just-sent message. The draft already
      // mirrors the transcript, so there's nothing to gain here — bail.
      if (this.#sendInFlight) return;

      // Registry-build turns keep a deliberately calm surface LIVE — the SSE
      // handler drops tool:product_card_added while #registryBuildActive. This
      // replay must mirror that, or an internal grounding search the model ran
      // during the intake/build (whose cards the user never saw) resurfaces on
      // reopen as a row of random product cards + an "Open full page" box under
      // an intake question. Flag each turn (user message → next user message)
      // whose tool rows include the build-flow kinds; birch_search rows in
      // those turns are skipped by both the handle prefetch and the walk below.
      const BUILD_FLOW_KINDS = new Set(['ask_registry_question', 'build_registry_draft', 'rebuild_registry']);
      /** @type {number[]} */
      const turnOfMessage = [];
      /** @type {Set<number>} */
      const buildFlowTurns = new Set();
      {
        let turn = 0;
        for (const m of messages) {
          if (m.role === 'user') turn++;
          turnOfMessage.push(turn);
          if (m.role === 'tool' && m.tool_output) {
            try {
              const k = JSON.parse(m.tool_output)?.kind;
              if (BUILD_FLOW_KINDS.has(k)) buildFlowTurns.add(turn);
            } catch { /* ignore */ }
          }
        }
      }
      const isBuildFlowTurn = (/** @type {number} */ idx) => {
        const turn = turnOfMessage[idx];
        return turn !== undefined && buildFlowTurns.has(turn);
      };

      // Collect LushLife product ids AND Birch handles across the whole
      // transcript so we make a single bulk lookup per source instead of
      // per-message round-trips (item #10). Both kinds now render inline cards.
      /** @type {string[]} */
      const lushIds = [];
      /** @type {string[]} */
      const birchHandles = [];
      for (let mi = 0; mi < messages.length; mi++) {
        const m = messages[mi];
        if (m.tool_output) {
          try {
            const out = JSON.parse(m.tool_output);
            if ((out.kind === 'lushlife_fallback' || out.kind === 'web_fallback') && Array.isArray(out.productIds)) {
              // web_fallback = non-registry web-search results; same external
              // card shape, re-fetched the same way (retailer link included).
              for (const id of out.productIds) if (!lushIds.includes(id)) lushIds.push(id);
            } else if (out.kind === 'birch_search' && Array.isArray(out.handles) && !isBuildFlowTurn(mi)) {
              for (const h of out.handles) if (!birchHandles.includes(h)) birchHandles.push(h);
            }
          } catch { /* ignore */ }
        }
      }
      /** @type {Map<string, Record<string, unknown>>} */
      const lushById = new Map();
      if (lushIds.length > 0) {
        try {
          const r = await fetch(`${PROXY}/lushlife-products?ids=${encodeURIComponent(lushIds.join(','))}`);
          const j = await r.json();
          for (const p of (j.products ?? [])) lushById.set(String(p.handle), p);
        } catch { /* ignore */ }
      }
      if (stale()) return;
      // Birch cards are referenced by handle in D1; re-fetch their card data so
      // we can re-render them inline (parity with LushLife above).
      /** @type {Map<string, Record<string, unknown>>} */
      const birchByHandle = new Map();
      if (birchHandles.length > 0) {
        try {
          const r = await fetch(`${PROXY}/birch-products?handles=${encodeURIComponent(birchHandles.join(','))}`);
          const j = await r.json();
          for (const p of (j.products ?? [])) birchByHandle.set(String(p.handle), p);
        } catch { /* ignore */ }
        // The transcript HAS product results but the card lookup came back with
        // nothing (offline, 5xx, proxy hiccup). Rebuilding now would render a
        // card-less transcript AND overwrite the draft with it (see the
        // saveDraft at the end), permanently losing results the user could see
        // a moment ago. Keep the local replay and retry on the next page load.
        if (birchByHandle.size === 0) return;
      }
      if (stale()) return;

      // Frontend-only refinement chips are NOT part of the durable D1
      // transcript (they're SSE-derived display entries), so the rebuild
      // below would silently drop them — which is what made the post-build
      // followup options vanish after the build_complete → /pages/registry-edit
      // redirect rehydrated from the server. Each chip set belongs under the
      // assistant summary that immediately follows it in the log, so key the
      // chips by that summary's text and re-attach them as the rebuild replays
      // the matching assistant message below.
      // The same is true of every other SSE-derived display entry: refinement
      // prompts, the coherence-review panel, disambiguation pills, the
      // "search the web" offer, and guided-intake chips. None exist in D1, so
      // the rebuild dropped them AND then overwrote the draft without them —
      // they were gone for good after any page change. Bucket them all by the
      // assistant summary they belong under, keyed by that summary's text, and
      // re-attach with the right renderer as the rebuild replays it.
      //
      // Anchor direction differs by kind: refinement/coherence are emitted
      // BEFORE their summary streams (so look forward), while pills, the
      // web-search offer and intake chips render onto the bubble that's already
      // on screen (so look backward). Each falls back to the other direction.
      const FORWARD_ANCHORED = new Set(['refinement_prompts', 'coherence_review']);
      const BACKWARD_ANCHORED = new Set(['pills', 'web_search_pill', 'intake_question']);
      /** @type {Map<string, Array<{ kind: string, data: Record<string, any> }>>} */
      const chipsBySummary = new Map();
      for (let i = 0; i < this.#displayLog.length; i++) {
        const entry = this.#displayLog[i];
        if (!entry || !entry.data) continue;
        const kind = String(entry.kind);
        if (!FORWARD_ANCHORED.has(kind) && !BACKWARD_ANCHORED.has(kind)) continue;
        // Next assistant_text in the same turn.
        const lookForward = () => {
          for (let j = i + 1; j < this.#displayLog.length; j++) {
            const e = this.#displayLog[j];
            if (e?.kind === 'assistant_text') return String(e.text);
            if (e?.kind === 'user') break;
          }
          return '';
        };
        // Most recent assistant_text before this entry (any turn — the summary
        // it rendered onto may predate the current turn's first delta).
        const lookBackward = () => {
          for (let j = i - 1; j >= 0; j--) {
            const e = this.#displayLog[j];
            if (e?.kind === 'assistant_text') return String(e.text);
          }
          return '';
        };
        const anchor = FORWARD_ANCHORED.has(kind)
          ? (lookForward() || lookBackward())
          : (lookBackward() || lookForward());
        const bucket = chipsBySummary.get(anchor) ?? [];
        bucket.push({ kind, data: /** @type {Record<string, any>} */ (entry.data) });
        chipsBySummary.set(anchor, bucket);
      }

      /**
       * Re-render one preserved display entry onto its assistant bubble.
       * @param {string} kind @param {Record<string, any>} data @param {HTMLElement} el
       */
      const reattachChip = (kind, data, el) => {
        if (kind === 'coherence_review') this.#renderCoherenceReview(el, data, { replay: true });
        else if (kind === 'pills') this.#renderDisambiguationPills(el, data);
        else if (kind === 'web_search_pill') this.#renderWebSearchPill(el, data);
        else if (kind === 'intake_question') this.#renderIntakeQuestion(el, data, { replay: true });
        else this.#renderRefinementPrompts(el, data, { replay: true });
      };

      // Clear any draft replay we did optimistically; server state wins.
      this.#messageList.innerHTML = '';
      this.#displayLog = [];

      // Tool messages from D1 arrive BETWEEN the assistant's intro "Let me
      // look that up…" message and the post-tool descriptive answer
      // "Here are the strollers…". Attaching the Show-Results box to
      // whichever assistant message happens to be in the DOM at the time of
      // tool replay would land it on the intro (wrong — appears above the
      // actual answer). Defer the attachment until the NEXT assistant
      // message that carries real content (the post-tool answer) is
      // appended, then anchor the box there.
      /** @type {Array<{ kind: 'birch'|'lushlife'|'navigate', handles?: string[], productIds?: string[], relatedLink?: Record<string, any>, link?: Record<string, any> }>} */
      const pendingToolDeferred = [];

      const drainPendingTools = (/** @type {HTMLElement} */ targetEl) => {
        while (pendingToolDeferred.length > 0) {
          const t = pendingToolDeferred.shift();
          if (!t) continue;
          if (t.kind === 'birch' && t.handles) {
            const cardsContainer = targetEl.querySelector('.birch-cards') ?? this.#ensureCardsContainer(targetEl);
            for (const h of t.handles) {
              const card = birchByHandle.get(String(h));
              if (card) {
                this.#renderBirchCard(/** @type {HTMLElement} */ (cardsContainer), card);
                this.#displayLog.push({ kind: 'birch_inline', card: this.#slimBirchCard(card) });
              }
            }
            this.#renderShowResultsBox(targetEl, t.handles);
            this.#displayLog.push({ kind: 'birch_results', handles: t.handles });
            // Related "Browse all <category>" link, below the cards (suppressed
            // when the user is already on that collection page).
            if (t.relatedLink?.url && this.#renderNavigateButton(targetEl, t.relatedLink, { skipPageCheck: true })) {
              this.#displayLog.push({ kind: 'navigate', url: t.relatedLink.url, label: t.relatedLink.label, title: t.relatedLink.title });
            }
          } else if (t.kind === 'lushlife' && t.productIds) {
            const cardsContainer = targetEl.querySelector('.birch-cards') ?? this.#ensureCardsContainer(targetEl);
            for (const id of t.productIds) {
              const card = lushById.get(String(id));
              if (card) this.#renderExternalCard(/** @type {HTMLElement} */ (cardsContainer), card);
            }
            this.#displayLog.push({ kind: 'lushlife_results', productIds: t.productIds });
          } else if (t.kind === 'navigate' && t.link?.url) {
            if (this.#renderNavigateButton(targetEl, t.link, { skipPageCheck: true })) {
              this.#displayLog.push({ kind: 'navigate', url: t.link.url, label: t.link.label, title: t.link.title });
            }
          }
        }
      };

      for (let mi = 0; mi < messages.length; mi++) {
        const m = messages[mi];
        if (m.role === 'user' && m.content) {
          this.#appendMessage('user', m.content);
          this.#displayLog.push({ kind: 'user', text: m.content });
        } else if (m.role === 'assistant' && m.content) {
          const el = this.#appendMessage('assistant', m.content);
          // Re-attach any refinement / coherence chips that belonged under this
          // summary. Log them BEFORE the assistant_text (their live order) so a
          // later replay buffers and flushes them onto this same bubble.
          const chips = chipsBySummary.get(String(m.content));
          if (chips) {
            for (const { kind, data } of chips) {
              this.#displayLog.push({ kind, data });
              reattachChip(kind, data, /** @type {HTMLElement} */ (el));
            }
            chipsBySummary.delete(String(m.content));
          }
          this.#displayLog.push({ kind: 'assistant_text', text: m.content });
          // Anchor any deferred tool output to THIS assistant bubble (the
          // post-tool answer). If a turn has multiple assistant messages
          // (intro + answer), the answer always comes after the tool — so
          // we drain on the second one we see.
          drainPendingTools(/** @type {HTMLElement} */ (el));
        } else if (m.role === 'tool' && m.tool_output) {
          let out;
          try { out = JSON.parse(m.tool_output); } catch { continue; }
          if (out.kind === 'birch_search' && Array.isArray(out.handles)) {
            // Skip searches from build-flow turns — never shown live (calm
            // surface), so they must not resurface here. See pre-scan above.
            if (isBuildFlowTurn(mi)) continue;
            pendingToolDeferred.push({ kind: 'birch', handles: out.handles, relatedLink: out.relatedLink });
          } else if ((out.kind === 'lushlife_fallback' || out.kind === 'web_fallback') && Array.isArray(out.productIds)) {
            pendingToolDeferred.push({ kind: 'lushlife', productIds: out.productIds });
          } else if (out.kind === 'navigate' && out.ok && out.url) {
            // LLM-driven navigate button — reconstruct it under the post-tool reply.
            pendingToolDeferred.push({ kind: 'navigate', link: { url: out.url, label: out.label, title: out.title } });
          }
        }
      }

      // If the transcript ended with a tool result but no following
      // assistant message (rare — only if the stream got cut mid-turn),
      // fall back to attaching to the last assistant bubble so the user
      // still sees the box.
      if (pendingToolDeferred.length > 0) {
        // …and if the turn has NO assistant bubble at all yet (the answer text
        // hadn't flushed to D1 when the user navigated), spin up an empty one
        // to hold them. Without this the cards were dropped here and then
        // written out of the draft below — results vanishing on a page change.
        const lastAssistant = /** @type {HTMLElement} */ (
          this.#messageList.querySelector('.birch-msg--assistant:last-of-type')
          || this.#appendMessage('assistant', '')
        );
        drainPendingTools(lastAssistant);
      }

      // Any chip sets whose summary wasn't in the server transcript (e.g. the
      // summary stream was cut before D1 flushed) still attach to the last
      // assistant bubble so the followup options don't vanish.
      if (chipsBySummary.size > 0) {
        const lastAssistant = /** @type {HTMLElement | null} */ (this.#messageList.querySelector('.birch-msg--assistant:last-of-type'));
        for (const bucket of chipsBySummary.values()) {
          for (const { kind, data } of bucket) {
            if (lastAssistant) reattachChip(kind, data, lastAssistant);
            this.#displayLog.push({ kind, data });
          }
        }
      }

      saveDraft(this.#sessionId, this.#displayLog);
      // Transcript rebuilt (or confirmed empty) — reconcile the starter pills:
      // drop them if a conversation loaded, (re)show them if it's still empty.
      this.#syncEmptyState();
    } catch (err) {
      console.warn('[birch-ai] rehydrate failed:', err);
    } finally {
      // Every early exit above (empty transcript, freshness bail, send in
      // flight, network error) has to retire the loading skeleton too, or a
      // chat with nothing to replay would sit on placeholder bars forever.
      // Skipped when a newer session took over: it owns the surface now, and
      // its own skeleton is the one on screen.
      if (!stale()) this.#syncEmptyState();
    }
  }

  /** Replay the in-memory display log into the DOM (used for in-progress draft restore). */
  #replayDisplayLog() {
    this.#messageList.innerHTML = '';
    /** @type {HTMLElement | null} */
    let lastAssistantEl = null;
    // Refinement / coherence chips are emitted (and therefore logged) BEFORE
    // the assistant summary they belong under — the SSE `refinement_prompts` /
    // `coherence_review` events land ahead of the summary's `text_delta`s. So we
    // can't render them inline: at that point the only assistant bubble is the
    // previous turn's (or none). Buffer them and flush onto the NEXT assistant
    // bubble (their summary).
    /** @type {Array<{ kind: string, data: Record<string, any> }>} */
    let pendingRefinements = [];
    // Cards / pills / nav buttons hang off an assistant bubble. They are logged
    // in SSE arrival order, and a tool-first turn (search runs before any text
    // streams) logs them BEFORE the assistant_text they belong under — so
    // requiring an existing bubble silently dropped every card on replay, which
    // is how results vanished after navigating. Mint an empty bubble instead,
    // and let the assistant_text that follows fill it in — that reproduces the
    // live layout (one bubble, text above its card rail) rather than stacking a
    // card-only bubble above a text-only one.
    let mintedEmptyBubble = false;
    const hostBubble = () => {
      if (!lastAssistantEl) {
        lastAssistantEl = this.#appendMessage('assistant', '');
        mintedEmptyBubble = true;
      }
      return /** @type {HTMLElement} */ (lastAssistantEl);
    };
    const flushRefinements = (/** @type {HTMLElement | null} */ targetEl) => {
      if (!targetEl) return;
      for (const { kind, data } of pendingRefinements) {
        if (kind === 'coherence_review') this.#renderCoherenceReview(targetEl, data, { replay: true });
        else this.#renderRefinementPrompts(targetEl, data, { replay: true });
      }
      pendingRefinements = [];
    };
    for (const entry of this.#displayLog) {
      if (entry.kind === 'user') {
        this.#appendMessage('user', String(entry.text));
        lastAssistantEl = null;
        mintedEmptyBubble = false;
      } else if (entry.kind === 'assistant_text') {
        /** @type {HTMLElement} */
        let el;
        if (lastAssistantEl && mintedEmptyBubble) {
          // Fill the bubble the cards above already mounted into.
          el = lastAssistantEl;
          const txt = el.querySelector('.birch-msg__text');
          if (txt) txt.innerHTML = this.#renderMarkdown(String(entry.text));
          mintedEmptyBubble = false;
        } else {
          el = this.#appendMessage('assistant', String(entry.text));
          lastAssistantEl = el;
        }
        flushRefinements(el);
      } else if (entry.kind === 'birch_inline' && entry.card) {
        const host = hostBubble();
        const cardsContainer = host.querySelector('.birch-cards') ?? this.#ensureCardsContainer(host);
        this.#renderBirchCard(/** @type {HTMLElement} */ (cardsContainer), /** @type {Record<string, unknown>} */ (entry.card));
      } else if (entry.kind === 'birch_results' && Array.isArray(entry.handles)) {
        this.#renderShowResultsBox(hostBubble(), /** @type {string[]} */ (entry.handles));
      } else if (entry.kind === 'lushlife_inline' && entry.card) {
        const host = hostBubble();
        const cardsContainer = host.querySelector('.birch-cards') ?? this.#ensureCardsContainer(host);
        this.#renderExternalCard(/** @type {HTMLElement} */ (cardsContainer), /** @type {Record<string, unknown>} */ (entry.card));
      } else if (entry.kind === 'pills' && entry.data) {
        this.#renderDisambiguationPills(hostBubble(), /** @type {Record<string, unknown>} */ (entry.data));
      } else if (entry.kind === 'web_search_pill' && entry.data) {
        this.#renderWebSearchPill(hostBubble(), /** @type {Record<string, unknown>} */ (entry.data));
      } else if (entry.kind === 'navigate' && entry.url) {
        this.#renderNavigateButton(hostBubble(), /** @type {Record<string, unknown>} */ (entry), { skipPageCheck: true });
      } else if ((entry.kind === 'refinement_prompts' || entry.kind === 'coherence_review') && entry.data) {
        // Buffer until the summary bubble appears (see flushRefinements). This
        // is what keeps the post-build chips (refinement + coherence review)
        // alive across the build_complete → /pages/registry-edit redirect.
        pendingRefinements.push({ kind: String(entry.kind), data: /** @type {Record<string, any>} */ (entry.data) });
      } else if (entry.kind === 'intake_question' && entry.data) {
        // Guided intake chips survive a mid-intake page reload. The assistant
        // usually emits no summary text for an intake turn, so attach to the
        // current assistant bubble or spin up an empty one to hold the chips.
        this.#renderIntakeQuestion(hostBubble(), /** @type {Record<string, any>} */ (entry.data), { replay: true });
      } else if (entry.kind === 'cart_updated') {
        // Restore the success receipt the user saw before refresh.
        const action = String(entry.action ?? 'update');
        const receipt = document.createElement('div');
        receipt.className = 'birch-receipt birch-receipt--success';
        receipt.textContent = action === 'add' ? '✓ Added to your cart'
          : action === 'add_bundle' ? '✓ Bundle added to your cart'
          : action === 'remove' ? '✓ Removed from your cart'
          : action === 'discount' ? '✓ Discount applied'
          : '✓ Cart updated';
        this.#messageList.appendChild(receipt);
      }
    }
    // Fallback: chips whose summary never streamed (turn cut short) still
    // attach to the last assistant bubble so they don't vanish.
    flushRefinements(lastAssistantEl);
  }

  /**
   * Build the product scroller: a flex wrapper holding the `.birch-cards` rail
   * (unchanged native scroll) plus minimal prev/next arrows and drag-to-scroll,
   * both desktop-only. Returns the inner rail callers append cards to.
   * @returns {{ wrap: HTMLElement, cards: HTMLElement }}
   */
  #buildCardsScroller() {
    const wrap = document.createElement('div');
    wrap.className = 'birch-cards-wrap';
    const cards = document.createElement('div');
    cards.className = 'birch-cards';
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'birch-cards-arrow birch-cards-arrow--prev';
    prev.setAttribute('aria-label', 'Scroll products left');
    prev.innerHTML = '‹';
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'birch-cards-arrow birch-cards-arrow--next';
    next.setAttribute('aria-label', 'Scroll products right');
    next.innerHTML = '›';
    wrap.append(prev, cards, next);
    this.#wireCardArrows(cards, prev, next);
    this.#wireDragScroll(cards);
    return { wrap, cards };
  }

  /** @param {HTMLElement} assistantEl */
  #ensureCardsContainer(assistantEl) {
    let c = assistantEl.querySelector('.birch-cards');
    if (!c) {
      const { wrap, cards } = this.#buildCardsScroller();
      assistantEl.appendChild(wrap);
      c = cards;
    }
    return /** @type {HTMLElement} */ (c);
  }

  /**
   * Toggle the prev/next arrows by scroll position and wire their clicks. The
   * rail's own overflow scroll is untouched — arrows just nudge scrollLeft.
   * @param {HTMLElement} scroller @param {HTMLElement} prev @param {HTMLElement} next
   */
  #wireCardArrows(scroller, prev, next) {
    const update = () => {
      // 1px slack: large enough to absorb sub-pixel scroll rounding, small
      // enough that there's never a position where BOTH arrows hide (a wider
      // slack on both ends left a dead zone near the corners). For any real
      // overflow, at least one direction is always available.
      const EPS = 1;
      const max = scroller.scrollWidth - scroller.clientWidth;
      const overflowing = max > EPS;
      prev.classList.toggle('is-visible', overflowing && scroller.scrollLeft > EPS);
      next.classList.toggle('is-visible', overflowing && scroller.scrollLeft < max - EPS);
    };
    // Defer to the next frame so reads happen AFTER the flex layout settles —
    // reading scrollWidth/clientWidth synchronously when cards are appended can
    // catch the rail mid-reflow (no overflow yet), which made the arrows only
    // appear once the user scrolled. rAF guarantees a post-layout measurement.
    const schedule = () => requestAnimationFrame(update);
    const step = () => Math.max(160, scroller.clientWidth * 0.8);
    prev.addEventListener('click', () => scroller.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => scroller.scrollBy({ left: step(), behavior: 'smooth' }));
    scroller.addEventListener('scroll', update, { passive: true });
    scroller.addEventListener('pointerenter', schedule);
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(schedule).observe(scroller);
    // Cards stream in over SSE — re-evaluate as they're appended.
    new MutationObserver(schedule).observe(scroller, { childList: true });
    // Recompute once the rail first becomes visible (panel opens after creation).
    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) schedule(); })
        .observe(scroller);
    }
    schedule();
  }

  /**
   * Click-and-drag horizontal scrolling for mouse users (desktop). Touch is
   * left to native momentum scrolling. A drag past a small threshold cancels
   * the click so it never opens the product link underneath.
   * @param {HTMLElement} scroller
   */
  #wireDragScroll(scroller) {
    let down = false, moved = false, startX = 0, startLeft = 0, pointerId = -1;
    const THRESHOLD = 5;
    scroller.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return; // native touch scroll handles this
      down = true; moved = false; pointerId = e.pointerId;
      startX = e.clientX; startLeft = scroller.scrollLeft;
    });
    scroller.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < THRESHOLD) return;
      moved = true;
      scroller.classList.add('is-dragging');
      try { scroller.setPointerCapture(pointerId); } catch { /* ignore */ }
      scroller.scrollLeft = startLeft - dx;
      e.preventDefault();
    });
    const end = () => {
      if (!down) return;
      down = false;
      scroller.classList.remove('is-dragging');
      if (pointerId !== -1) { try { scroller.releasePointerCapture(pointerId); } catch { /* ignore */ } pointerId = -1; }
    };
    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointercancel', end);
    // Stop the browser's native image ghost-drag from hijacking the gesture.
    scroller.addEventListener('dragstart', (e) => { if (down) e.preventDefault(); });
    // Capture-phase: swallow the click that follows a real drag so the card
    // link doesn't navigate. Reset the flag for the next interaction.
    scroller.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }

  async #send() {
    const text = this.#input.value.trim();
    if (!text) return;
    this.#input.value = '';

    // A new turn makes the standing "Refine your registry" question stale —
    // disable it whether the user clicked a chip or typed their own follow-up.
    this.#consumeRefinementPrompts();
    // Same for a standing guided-intake question.
    this.#consumeIntakeQuestions();

    // Queue-while-busy: if the previous stream is still running, buffer this
    // message instead of cancelling the in-flight turn. The queue drains on
    // the SSE `done` event of the current turn.
    if (this.#sendInFlight) {
      this.#queuedMessages.push(text);
      this.#renderQueuedBadge(text);
      return;
    }

    this.#sendInFlight = true;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#responseAnchor = this.#appendMessage('user', text);
    this.#displayLog.push({ kind: 'user', text });
    // Persist the session id + draft NOW, at the start of the turn. A normal
    // AI turn otherwise never calls persistSession (only the handoff / registry-
    // redirect paths do), so for guests the session id — minted fresh in memory
    // by loadOrCreateSession — was never written to localStorage. On refresh a
    // new id was generated, orphaning the draft (keyed by the old id) and making
    // the whole conversation vanish. Writing both here keeps them in lockstep so
    // a refresh or navigation mid-turn restores the chat.
    persistSession(this.#sessionId, this.#mode, this.#lastSeq);
    saveDraft(this.#sessionId, this.#displayLog);
    // Index this chat for the device-local guest history. Signed-in customers
    // use the durable server-side list instead, so skip them here. Chip = the
    // session's first user message; count = number of user turns so far.
    if (!this.dataset.customerId) {
      const firstUser = this.#displayLog.find((e) => e.kind === 'user');
      const chip = String(firstUser ? firstUser.text : text).slice(0, 80);
      const userTurns = this.#displayLog.filter((e) => e.kind === 'user').length;
      recordSession(this.#sessionId, chip, userTurns);
    }

    // Immediate "thinking" loader — gives the user feedback the instant they
    // send, covering the gap before the first SSE event (which can be 5–10s
    // for a registry build's Sonnet planner call). Removed the moment any
    // visible reply content appears (text, cards, build progress, handoff
    // banner), or when the turn ends. Not added to #displayLog — it's
    // transient and must never be replayed on rehydrate.
    const thinkingEl = document.createElement('div');
    thinkingEl.className = 'birch-thinking';
    thinkingEl.setAttribute('aria-label', 'BirchAI is thinking');
    thinkingEl.innerHTML =
      '<span class="birch-thinking__dot"></span>' +
      '<span class="birch-thinking__dot"></span>' +
      '<span class="birch-thinking__dot"></span>';
    this.#messageList.appendChild(thinkingEl);
    this.#scrollMessagesToEnd();
    let thinkingRemoved = false;
    const removeThinking = () => {
      if (thinkingRemoved) return;
      thinkingRemoved = true;
      thinkingEl.remove();
    };

    // Registry-build gap-filler. In a build turn the AI streams a short intro
    // ("I'll build your baby registry…") and only THEN calls the build tool.
    // The tool fires `registry:planning` the instant it enters, but the real,
    // VISIBLE progress panel only appears later, on `registry:build_started` —
    // after a ~5–10s Sonnet planner call. On a retry that planner runs twice
    // with streamed "let me try again" text in between. The intro/retry text
    // retires the thinking loader, so that whole stretch would otherwise show
    // nothing. Two mechanisms bridge it:
    //   1. A persistent loader shown directly on `registry:planning` (the
    //      authoritative server signal), kept up through streamed retry text
    //      and retired only when real progress (build_started) lands or the
    //      turn ends. This is what covers the planner dead air for ANY build,
    //      including a free-typed "build my registry" where no intent chip set
    //      registryBuildActive.
    //   2. A secondary 700ms idle loader (below), re-armed on every SSE event,
    //      that bridges the tiny intro-text → first-event gap when the build
    //      intent is known up front (chip / search-bar handoff).
    const registryBuildActive = this.#pendingIntent === 'registry_builder' || this.#registryBuildActive;
    if (registryBuildActive) this.#registryBuildActive = true;
    /** @type {HTMLElement | null} */ let buildGapEl = null;
    /** @type {ReturnType<typeof setTimeout> | null} */ let buildGapTimer = null;
    let buildProgressSeen = false;
    // True between `registry:planning` and the first real progress event. While
    // set, the loader is persistent (the idle re-arm below leaves it alone).
    let buildPlanningActive = false;
    const clearBuildGapTimer = () => {
      if (buildGapTimer !== null) { clearTimeout(buildGapTimer); buildGapTimer = null; }
    };
    const removeBuildGap = () => {
      clearBuildGapTimer();
      if (buildGapEl) { buildGapEl.remove(); buildGapEl = null; }
    };
    const showBuildGap = () => {
      if (buildGapEl || buildProgressSeen) return;
      attachAssistant();
      buildGapEl = document.createElement('div');
      buildGapEl.className = 'birch-build-pending';
      buildGapEl.innerHTML =
        '<span class="birch-build-pending__spinner" aria-hidden="true"></span>' +
        '<span class="birch-build-pending__label">Setting up your registry…</span>';
      assistantEl.appendChild(buildGapEl);
      this.#scrollMessagesToEnd();
    };
    // Called at the top of every SSE event: a quiet stretch (no events) while
    // a build is active and progress hasn't started yet re-arms the loader.
    const noteBuildActivity = () => {
      // Once `registry:planning` has put up the persistent loader, leave it
      // alone — otherwise the retry text's deltas would flicker it off/on.
      if (!registryBuildActive || buildProgressSeen || buildPlanningActive) return;
      removeBuildGap();
      buildGapTimer = setTimeout(showBuildGap, 700);
    };

    // Reset per-turn redirect tracking
    this.#redirectedThisTurn = false;
    this.#pendingBirchHandles = [];

    const assistantEl = document.createElement('div');
    assistantEl.className = 'birch-msg birch-msg--assistant';
    assistantEl.innerHTML = '<div class="birch-msg__text"></div>';
    const { wrap: cardsWrap, cards: cardsContainer } = this.#buildCardsScroller();
    assistantEl.appendChild(cardsWrap);
    let assistantAttached = false;
    /** @type {HTMLElement | null} */
    let searchingEl = null;
    const attachAssistant = () => {
      // Any path that surfaces visible assistant content routes through here,
      // so this is the single place to retire the "thinking" loader.
      removeThinking();
      if (!assistantAttached) {
        assistantAttached = true;
        this.#messageList.appendChild(assistantEl);
      }
    };

    try {
      const basePageContext = /** @type {any} */ (window).BirchAI?.context ?? { template: 'index', url: location.href, referrer: '' };
      const pageContext = this.#pendingMetadata
        ? { ...basePageContext, registryBuilder: this.#pendingMetadata }
        : basePageContext;

      // Guest registry sessions: if this turn could touch the registry, make
      // sure the signed-out shopper has an owner identity BEFORE the request —
      // the registry tools run inside it, and without the cookie they'd refuse.
      // Scoped to registry-intent turns so ordinary product chat for a
      // signed-out visitor still costs zero extra requests.
      const registryIntent =
        this.#pendingIntent === 'registry_builder' ||
        pageContext.intent === 'registry_builder' ||
        /\/pages\/registry-(edit|create)\b/.test(location.pathname) ||
        !!this.#pendingRegistryId ||
        !!(/** @type {any} */ (window).__birchActiveRegistryId) ||
        !!this.#pendingStartNew;
      if (registryIntent) await this.#ensureGuestRegistrySession();

      const resp = await fetch(this.#withGuestToken(`${PROXY}/chat`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          sessionId: this.#sessionId,
          customerId: this.dataset.customerId || undefined,
          pageContext,
          ...junoAnalyticsContext(),
          juno_session_id: window.BirchAI?.junoTelemetry?.searchContext?.().juno_session_id,
          // Slice 6 — per-turn selection hint. Cleared after send.
          ...(this.#pendingSelection.length > 0 && { selection: this.#pendingSelection }),
          // Slice R-builder — sticky intent (e.g. 'registry_builder'). The
          // worker persists it on the session DO so subsequent turns keep
          // the registry-builder skill pack active even after navigation.
          ...(this.#pendingIntent && { intent: this.#pendingIntent }),
          // Phase 10 — build mode (guided / full_build). Worker persists it on
          // the session DO so the guided wizard survives the multi-turn Q&A.
          ...(this.#pendingMode && { mode: this.#pendingMode }),
          // Registry target signals (mutually exclusive):
          //   • startNew — the build buttons sent "create a fresh registry even if
          //     others exist" (no registry in view). Suppresses any reference.
          //   • registryId — otherwise, the registry the user is viewing/working
          //     on: an explicit per-turn hint from birch:open-with-query, else the
          //     registry-edit page's live getter (window.__birchActiveRegistryId).
          //     Passed on EVERY call so the AI edits the right one.
          ...(this.#pendingStartNew
            ? { startNew: true }
            : ((this.#pendingRegistryId || /** @type {any} */ (window).__birchActiveRegistryId)
                ? { registryId: this.#pendingRegistryId || /** @type {any} */ (window).__birchActiveRegistryId }
                : {})),
          // One-shot web-search opt-in (the no-results "Search the web" button).
          ...(this.#pendingWebSearch && { webSearch: true }),
          // One-shot confirmation token from a cancel/return card — out-of-band
          // so it never shows in the chat or the persisted transcript.
          ...(this.#pendingConfirmation && { confirmationToken: this.#pendingConfirmation }),
          // Per-turn "Find similar" click context — the clicked card's identity.
          ...(this.#pendingFindSimilar && { findSimilar: this.#pendingFindSimilar }),
        }),
        signal: this.#abortController.signal,
      });
      // Clear the per-turn hints regardless of HTTP outcome — the user has
      // already seen the visual chip; the next turn should not repeat it.
      this.#pendingSelection = [];
      this.#pendingIntent = '';
      this.#pendingMode = '';
      this.#pendingStartNew = false;
      this.#pendingWebSearch = false;
      this.#pendingMetadata = null;
      this.#pendingFindSimilar = null;
      this.#pendingRegistryId = '';
      this.#pendingConfirmation = '';

      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      let textBuffer = '';
      // Buffered "Find external products" offer. The no_birch_results event
      // fires DURING the tool round — before the assistant's "we couldn't find
      // it" reply streams — so rendering the button there would flash it above
      // the still-empty bubble. We stash the offer and render it on `done`, so
      // it always lands BELOW the finished message.
      /** @type {Record<string, any> | null} */
      let pendingWebSearchOffer = null;
      const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader();

      let sseBuffer = '';
      let pendingEventType = '';
      let pendingData = '';

      const dispatchEvent = async (/** @type {string} */ eventType, /** @type {string} */ rawData) => {
        let data;
        try { data = JSON.parse(rawData); } catch { return; }

        // Re-arm the registry-build gap loader on every event so it only ever
        // surfaces during a quiet stretch (see noteBuildActivity above).
        noteBuildActivity();

        if (eventType === 'tool:search_started') {
          // Lightweight in-chat status so the user sees feedback immediately
          // (item #2). Removed the moment any other event arrives.
          attachAssistant();
          if (!searchingEl) {
            searchingEl = document.createElement('div');
            searchingEl.className = 'birch-searching';
            searchingEl.textContent = data.query ? `Searching for “${data.query}”…` : 'Searching…';
            assistantEl.insertBefore(searchingEl, assistantEl.firstChild);
          }
        } else if (eventType === 'text_delta') {
          attachAssistant();
          if (searchingEl) { searchingEl.remove(); searchingEl = null; }
          textBuffer += data.text ?? '';
          /** @type {HTMLElement} */ (assistantEl.querySelector('.birch-msg__text')).innerHTML = this.#renderMarkdown(textBuffer);
          this.#updateLastAssistantTextEntry(textBuffer);
          // Cross-surface mirror — the registry-create canvas (and any
          // other listener) can render the running text without owning its
          // own SSE reader.
          window.dispatchEvent(new CustomEvent('birch:assistant_text', { detail: { text: textBuffer } }));
        } else if (eventType === 'tool:product_card_added') {
          // During a registry build we deliberately keep the surface calm —
          // no product listings streaming in. The picks land on the registry
          // itself; the chat just shows soothing progress narration.
          if (this.#registryBuildActive) return;
          // BIRCH product card — render inline in chat, the same treatment as
          // LushLife cards. We also accumulate the handle so the "Open full
          // page" box can link to the full search-results page. There is no
          // auto-redirect anymore (see the `done` handler) — the user stays in
          // the conversation and opts into the full page if they want it.
          attachAssistant();
          if (searchingEl) { searchingEl.remove(); searchingEl = null; }
          this.#pendingBirchHandles.push(String(data.handle));
          if (data.handle && data.title) this.#titleByHandle.set(String(data.handle), String(data.title));
          this.#renderBirchCard(cardsContainer, data);
          this.#displayLog.push({ kind: 'birch_inline', card: this.#slimBirchCard(data) });
          if (!this.#redirectedThisTurn) {
            this.#redirectedThisTurn = true; // flag = "box rendered for this turn"
            this.#renderShowResultsBox(assistantEl, this.#pendingBirchHandles);
            this.#displayLog.push({ kind: 'birch_results', handles: this.#pendingBirchHandles });
          } else {
            // Subsequent Birch cards in the same turn: just update the
            // already-rendered box's count + pinned href.
            this.#updateShowResultsBox(assistantEl, this.#pendingBirchHandles);
          }
          saveDraft(this.#sessionId, this.#displayLog);
        } else if (eventType === 'tool:external_card_added') {
          // Keep registry builds calm — suppress streaming product cards.
          if (this.#registryBuildActive) return;
          // LushLife card — render inline in chat (item #7). The previous
          // implementation dropped this event entirely, so cards arrived in
          // the network tab but never appeared in the UI.
          attachAssistant();
          if (searchingEl) { searchingEl.remove(); searchingEl = null; }
          this.#renderExternalCard(cardsContainer, data);
          if (data.handle && data.title) this.#titleByHandle.set(String(data.handle), String(data.title));
          this.#displayLog.push({ kind: 'lushlife_inline', card: this.#slimExternalCard(data) });
          saveDraft(this.#sessionId, this.#displayLog);
        } else if (eventType === 'tool:no_birch_results') {
          // When Birch has nothing AND the worker offered an external search
          // (non-registry path), buffer the offer and render the button on
          // `done` — AFTER the assistant's "we couldn't find it" sentence has
          // streamed — so it always sits BELOW the message, not above it.
          // (When external results stream back they arrive via external_card_added.)
          // Three ways this offer can be wrong, and only the first is the
          // worker's call: the sentence wasn't shopping (data.offerWebSearch is
          // false — the worker's shopping-intent gate), a registry build owns
          // the canvas, or we're on a tracker page where nothing is for sale.
          // The last is checked here too so a theme running ahead of the worker
          // still can't show it — see #onTrackerSurface.
          if (data && data.offerWebSearch && !this.#registryBuildActive && !this.#onTrackerSurface()) {
            pendingWebSearchOffer = data;
          }
        } else if (eventType === 'tool:open_tracker') {
          // Navigate to the requested tracker page after a brief delay so the
          // AI's confirmation message has time to stream in.
          const trackerUrl = data?.url;
          if (trackerUrl) {
            setTimeout(() => { this.#navigateMinimizingOnMobile(trackerUrl); }, 900);
          }
        } else if (eventType === 'tool:disambiguation_pills') {
          attachAssistant();
          this.#renderDisambiguationPills(assistantEl, data);
          this.#displayLog.push({ kind: 'pills', data: this.#slimPills(data) });
          saveDraft(this.#sessionId, this.#displayLog);
          // Re-dispatch for surfaces that mirror the chips (e.g. the
          // registry-create build canvas — when kind='profile_question' the
          // canvas pulls the chips out of the chat panel and renders them in
          // the foreground so the user doesn't have to dig into the side
          // panel during a build).
          window.dispatchEvent(new CustomEvent('birch:disambiguation_pills', { detail: data }));
        } else if (eventType === 'tool:cart_action') {
          // Slice 8 — backend validated the action; we execute the Shopify
          // AJAX call here (uses the user's cart cookie). The handler emits
          // a synthetic `tool:cart_updated` on success which flows through
          // the unified #handleCartUpdated below.
          void this.#executeCartAction(/** @type {Record<string, unknown>} */ (data));
        } else if (eventType === 'tool:cart_updated') {
          // Dual-source per packages/shared/src/events.ts: frontend emits
          // this synthetically after Storefront AJAX success; future Plus /
          // B2B server-side cart flows would emit it over SSE. Both paths
          // converge here.
          void this.#handleCartUpdated(/** @type {Record<string, unknown>} */ (data));
        } else if (eventType === 'tool:wishlist_updated') {
          // Worker-emitted after the customer-wishlist metafield write
          // succeeds (the worker IS the authority for wishlist state).
          // Theme convention: `wishlist:update` (NOT `wishlist:refresh`).
          // WishlistStore's local cache reads from the metafield on `init()`
          // and on `wishlist:update` events; dispatching the proper event
          // signals listeners (heart icons, count badges) to re-render.
          const action = String(data.action ?? 'updated');
          document.dispatchEvent(new CustomEvent('wishlist:update', {
            bubbles: true,
            detail: { action, productId: data.handle, source: 'birch-ai' },
          }));
        } else if (eventType === 'tool:comparison_table') {
          attachAssistant();
          this.#renderComparisonTable(assistantEl, data);
        } else if (eventType === 'tool:navigate') {
          // Site navigation — the worker resolved a real on-site page and the
          // user chose a button UX (not an auto-redirect), so the conversation
          // is preserved. Render a clickable button into the chat; the user taps
          // to navigate. The button survives reload via the displayLog replay.
          attachAssistant();
          if (searchingEl) { searchingEl.remove(); searchingEl = null; }
          if (this.#renderNavigateButton(assistantEl, data)) {
            this.#displayLog.push({ kind: 'navigate', url: data.url, label: data.label, title: data.title });
            saveDraft(this.#sessionId, this.#displayLog);
          }
        } else if (eventType === 'tool:order_list') {
          attachAssistant();
          this.#renderOrderList(assistantEl, data.orders ?? []);
        } else if (eventType === 'tool:order_details') {
          attachAssistant();
          this.#renderOrderDetails(assistantEl, data.order);
        } else if (eventType === 'tool:confirmation_required') {
          attachAssistant();
          this.#renderConfirmationCard(assistantEl, data);
        } else if (eventType === 'tool:order_cancelled') {
          attachAssistant();
          // The worker sends outcome-accurate copy (auto-cancelled vs pending
          // team review vs awaiting a partner brand). Fallback for old events.
          this.#renderReceipt(assistantEl, data.receipt || "Order cancelled. Refund will follow Shopify's normal timeline.");
        } else if (eventType === 'tool:return_requested') {
          attachAssistant();
          this.#renderReceipt(assistantEl, data.receipt || "Return request submitted. You'll hear back soon.");
        } else if (eventType === 'handoff:waiting') {
          removeThinking();
          this.#enterHandoffMode('waiting', data.etaSeconds ?? 120);
        } else if (eventType === 'handoff:offline_fallback') {
          attachAssistant();
          this.#renderOfflineFallback(assistantEl, data);
        } else if (eventType === 'handoff:relayed') {
          // Server bypassed the LLM and forwarded the message to Slack.
          // Show a small ack so the user knows the send succeeded.
          attachAssistant();
          if (searchingEl) { searchingEl.remove(); searchingEl = null; }
          const msgTextEl = assistantEl.querySelector('.birch-msg__text');
          if (msgTextEl && !msgTextEl.textContent) {
            msgTextEl.textContent = data.queued
              ? 'Sent — your message will reach the agent the moment they pick up.'
              : 'Sent to the support team.';
          }
        } else if (eventType === 'handoff:ended') {
          this.#exitHandoffMode(data?.reason ?? 'agent_ended');
        } else if (eventType === 'registry:intake_question') {
          // Guided "Walk Me Through" intake: ONE question rendered as clickable
          // (optionally multi-select) chips, emitted BEFORE any build. Handled
          // ahead of the generic registry:* branch so it is NOT treated as
          // build progress (no sticky build panel, no registryBuildActive).
          attachAssistant();
          this.#renderIntakeQuestion(assistantEl, data);
          window.dispatchEvent(new CustomEvent('birch:registry:intake_question', { detail: data }));
        } else if (eventType && eventType.indexOf('registry:') === 0) {
          // Re-dispatch every registry:* event as a DOM CustomEvent so the
          // registry-edit page can render live progress without owning an
          // SSE reader of its own.
          window.dispatchEvent(new CustomEvent(`birch:${eventType}`, { detail: data }));
          // ── Visible in-chat progress ─────────────────────────────────────
          // The user wants to SEE what's happening during a build. We render
          // a single sticky "build progress" panel inside the assistant
          // bubble and append events as bullet rows (stage started, item
          // added, gap, complete).
          attachAssistant();
          this.#registryBuildActive = true;
          if (eventType === 'registry:build_started' && data && typeof data.registryId === 'string') {
            this.#buildingRegistryId = data.registryId;
            this.#updateSendMode();
          } else if (eventType === 'registry:build_complete' || eventType === 'registry:build_failed') {
            this.#buildingRegistryId = null;
            this.#updateSendMode();
          }
          if (eventType === 'registry:planning') {
            // The builder just entered: the ~5–10s Sonnet planner call (and any
            // retry of it) produces NO visible progress yet, and on a retry the
            // LLM streams "let me try again" text in between — so this stretch
            // would otherwise show nothing. Put up a PERSISTENT "setting up"
            // loader that survives the streamed retry text and is retired only
            // when real progress (build_started) lands or the turn ends.
            // Deliberately do NOT set buildProgressSeen here: no real progress
            // panel exists yet, and marking it would leave the planner's dead
            // air uncovered (the original bug — `registry:planning` used to set
            // buildProgressSeen and then render nothing, since the build panel
            // is only created on build_started).
            buildPlanningActive = true;
            showBuildGap();
          } else {
            // Real progress has started — retire the planning/gap loader for
            // good and render the sticky build-progress panel.
            buildProgressSeen = true;
            buildPlanningActive = false;
            removeBuildGap();
            if (eventType === 'registry:coherence_review') {
              // Sonnet's post-build review — surface as a separate panel with
              // clickable swap/drop/add action chips. NOT folded into the
              // build-progress feed because the user has already seen "build
              // complete" by this point and the proposals are a follow-up
              // refinement opportunity, not progress.
              this.#removeCoherenceThinking(assistantEl);
              this.#renderCoherenceReview(assistantEl, data);
            } else if (eventType === 'registry:coherence_thinking') {
              // Heartbeat while Sonnet thinks. Inserts a "Reviewing your
              // registry…" status row once; subsequent ticks just animate
              // the indicator without spawning more rows.
              this.#renderCoherenceThinking(assistantEl, data);
            } else if (eventType === 'registry:refinement_prompts') {
              // Build-first / refine-after: after the bulk insert, the builder
              // emits ONE follow-up question with 3–4 clickable answer pills.
              // Clicking a pill sends that answer back into chat to drive a
              // refinement turn — AI naturally generates the next question.
              this.#renderRefinementPrompts(assistantEl, data);
            } else {
              this.#renderRegistryProgress(assistantEl, eventType, data);
            }
          }
          // ── Deferred navigation ──────────────────────────────────────────
          // On build_complete, FLAG the redirect — but don't fire it yet.
          // The post-build summary text is still streaming; redirecting on a
          // fixed timer cut it off mid-sentence. The `done` handler below
          // executes the actual navigation once all text has flushed.
          if (eventType === 'registry:build_complete' && data && typeof data.registryId === 'string') {
            const alreadyThere =
              /\/pages\/registry-edit\b/.test(location.pathname) &&
              (new URLSearchParams(location.search).get('id') === data.registryId);
            if (!alreadyThere) this.#pendingRegistryEditId = data.registryId;
            // Already on the edit page (the builder handoff opens the chat
            // full screen right here) — there is no navigation to make the
            // finished registry visible, so flag the reveal instead. Fired at
            // `done` for the same reason the redirect is.
            else this.#pendingRegistryReveal = data.registryId;
          }
          // NOTE (2026-06-12): the old "navigate to the edit page on build_STARTED"
          // behaviour was REMOVED. It was based on a false premise — that the
          // build "runs to completion server-side in waitUntil regardless of this
          // page." Verified empirically: navigating here aborts the chat SSE
          // fetch, Cloudflare then cancels the request's waitUntil, and the ~165s
          // build dies before its single end-of-build persist → the edit page sat
          // on the "building…" banner forever with 0 items. The build MUST run in
          // this live connection, so we keep the user here watching it stream and
          // navigate only on build_complete (the deferred-nav above). See
          // REGISTRY-BUILDER-V3-REFACTOR-PLAN.md §4 bug (d).
        } else if (eventType === 'profile:fact_updated') {
          // Profile write from chat (clarifying-question chip flow). Silent
          // in chat UI — re-dispatched so other surfaces (profile-setup
          // badge, registry build canvas) can re-render.
          window.dispatchEvent(new CustomEvent('birch:profile:fact_updated', { detail: data }));
        } else if (eventType === 'done') {
          // Safety: if the turn produced no visible content at all, the loader
          // is still on screen — retire it now that the stream is finished.
          removeThinking();
          removeBuildGap();
          // Mirror the lifecycle to canvases / other surfaces so they can
          // exit "loading" states even when no registry:* events fired
          // (e.g. AI asked a question and stopped — no build happened).
          window.dispatchEvent(new CustomEvent('birch:turn_done', { detail: {} }));
          // Defensive: if a build-progress panel is still spinning (no
          // build_complete arrived before the stream ended), finalize it so
          // the loader doesn't persist.
          const panel = /** @type {HTMLElement | null} */ (assistantEl.querySelector('.birch-build-calm'));
          if (panel && !panel.classList.contains('birch-build-calm--done') && !panel.classList.contains('birch-build-calm--error')) {
            this.#stopBuildCalm();
            panel.classList.add('birch-build-calm--done');
            const lineEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bp-line]'));
            const subEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bp-sub]'));
            if (lineEl) lineEl.textContent = 'All done.';
            if (subEl) subEl.textContent = '';
          }
          // Defensive: if the coherence-thinking heartbeat row is still
          // present (Sonnet timed out without emitting a coherence_review),
          // remove it so the user doesn't see a perpetual spinner.
          this.#removeCoherenceThinking(assistantEl);
          // Render the buffered "Find external products" button now that the
          // whole reply has streamed — guarantees it sits below the sentence.
          if (pendingWebSearchOffer) {
            attachAssistant();
            this.#renderWebSearchPill(assistantEl, pendingWebSearchOffer);
            this.#displayLog.push({ kind: 'web_search_pill', data: { query: pendingWebSearchOffer.query } });
            saveDraft(this.#sessionId, this.#displayLog);
            pendingWebSearchOffer = null;
          }
          this.#scrollMessagesToEnd();
          if (this.#voiceMode) {
            await this.#speakText(textBuffer);
          }
          // NOTE (2026-06-20): the auto-redirect to the search results page on
          // `done` was REMOVED. Birch product results now render inline in the
          // chat (same as LushLife), and an "Open full page" box lets the user
          // opt into the full search-results page if they want it — we no
          // longer bounce them there automatically.
          // The no-navigation twin of the redirect below: the build finished on
          // the registry-edit page itself, so there is nothing to navigate to —
          // uncover the finished registry instead (drop the panel out of full
          // screen and bring "Currently in your registry" into view).
          if (this.#pendingRegistryReveal) {
            this.#pendingRegistryReveal = null;
            setTimeout(() => this.#revealRegistry({ auto: true }), 300);
          }
          // Registry build redirect — fired AFTER `done` (i.e. all text has
          // streamed) so the post-build summary lands in the chat history
          // intact before navigation. Tiny delay lets the final text_delta
          // commit to the displayLog.
          if (this.#pendingRegistryEditId) {
            const target = '/pages/registry-edit?id=' + encodeURIComponent(this.#pendingRegistryEditId);
            const id = this.#pendingRegistryEditId;
            this.#pendingRegistryEditId = null;
            setTimeout(() => {
              persistSession(this.#sessionId, this.#mode, this.#lastSeq);
              saveDraft(this.#sessionId, this.#displayLog);
              persistOpenState(true);
              location.href = target;
              void id; // satisfy linter — captured for clarity above
            }, 300);
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += value;
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            pendingEventType = line.slice(7).trim();
            pendingData = '';
          } else if (line.startsWith('data: ')) {
            pendingData += line.slice(6);
          } else if (line === '' && pendingEventType) {
            await dispatchEvent(pendingEventType, pendingData);
            pendingEventType = '';
            pendingData = '';
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        const txt = /** @type {HTMLElement} */ (assistantEl.querySelector('.birch-msg__text'));
        if (txt) txt.textContent = 'Something went wrong. Please try again.';
      }
    } finally {
      // Final safety net — covers error/abort exits where no `done` arrived.
      removeThinking();
      removeBuildGap();
      this.#sendInFlight = false;
      this.#responseAnchor = null;
      this.#drainQueue();
    }
  }

  /** @param {string} text */
  #renderQueuedBadge(text) {
    const wrap = document.createElement('div');
    wrap.className = 'birch-msg birch-msg--user birch-msg--queued';
    wrap.innerHTML = '<div class="birch-msg__text"></div><span class="birch-queued-label">Queued — will send when current reply finishes</span>';
    /** @type {HTMLElement} */ (wrap.querySelector('.birch-msg__text')).textContent = text;
    this.#messageList.appendChild(wrap);
    this.#scrollMessagesToEnd();
  }

  #drainQueue() {
    if (this.#queuedMessages.length === 0) return;
    const next = this.#queuedMessages.shift();
    if (!next) return;
    // Strip the "queued" badge marker on the message that's about to fire,
    // and re-render it as a normal user message via #send.
    const queuedEls = this.#messageList.querySelectorAll('.birch-msg--queued');
    if (queuedEls.length > 0) {
      const first = queuedEls[0];
      if (first) first.remove();
    }
    this.#input.value = next;
    // Recurse via #send so per-turn state resets cleanly.
    void this.#send();
  }

  /** @param {string} text */
  #updateLastAssistantTextEntry(text) {
    // Keep the last assistant_text entry in sync as text streams in.
    for (let i = this.#displayLog.length - 1; i >= 0; i--) {
      const entry = this.#displayLog[i];
      if (!entry) continue;
      if (entry.kind === 'assistant_text') {
        entry.text = text;
        saveDraft(this.#sessionId, this.#displayLog);
        return;
      }
      if (entry.kind === 'user') break;
    }
    this.#displayLog.push({ kind: 'assistant_text', text });
    saveDraft(this.#sessionId, this.#displayLog);
  }


  /**
   * Build-first / refine-after: ONE follow-up question with 3–4 answer pills.
   * Clicking a pill sends that answer back into chat to drive a refinement
   * turn; AI generates the next question card on the next turn. Reuses the
   * `.birch-coherence` class hierarchy so the chips inherit existing theme
   * styling without needing a new CSS block.
   * @param {HTMLElement} assistantEl
   * @param {Record<string, any>} data
   */
  /**
   * @param {HTMLElement} assistantEl
   * @param {Record<string, any>} data
   * @param {{ replay?: boolean }} [opts]
   */
  #renderRefinementPrompts(assistantEl, data, opts = {}) {
    const question = typeof data?.question === 'string' ? data.question.trim() : '';
    const options = Array.isArray(data?.options) ? data.options : [];
    if (!question || options.length === 0) return;
    const panel = document.createElement('div');
    // `--refine` marks this as a refinement-prompt panel so a subsequent send
    // (chip click OR a typed follow-up) can disable it — see #consumeRefinementPrompts.
    panel.className = 'birch-coherence birch-coherence--refine';
    panel.innerHTML =
      '<div class="birch-coherence__head">' +
        '<span class="birch-coherence__badge birch-coherence__badge--good">Refine your registry</span>' +
        '<span class="birch-coherence__summary">' + this.#escapeHtml(question) + '</span>' +
      '</div>' +
      '<div class="birch-coherence__row"></div>';
    const row = /** @type {HTMLElement} */ (panel.querySelector('.birch-coherence__row'));
    // Trim to a slim shape before persisting so localStorage doesn't bloat.
    const slimOptions = [];
    for (const o of options.slice(0, 4)) {
      if (!o || typeof o.label !== 'string' || typeof o.prompt !== 'string') continue;
      const label = String(o.label).trim();
      const prompt = String(o.prompt);
      slimOptions.push({ label, prompt });
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'birch-coherence__chip birch-coherence__chip--add';
      btn.textContent = label;
      btn.title = prompt;
      btn.addEventListener('click', () => {
        // Mark the chosen chip so #consumeRefinementPrompts can highlight it
        // when it disables the panel.
        btn.classList.add('birch-coherence__chip--selected');
        this.#input.value = prompt;
        this.#send();
      });
      row.appendChild(btn);
    }
    assistantEl.appendChild(panel);
    this.#scrollMessagesToEnd();
    // Persist to the display log so a redirect-and-rehydrate (e.g.
    // build_complete → /pages/registry-edit) restores the chips on the
    // next page load. `opts.replay` skips re-persisting when this method
    // is called from replayDisplayLog (avoids double-entry).
    if (!opts.replay) {
      this.#displayLog.push({
        kind: 'refinement_prompts',
        data: { question, options: slimOptions },
      });
      saveDraft(this.#sessionId, this.#displayLog);
    }
  }

  /**
   * Disable every active "Refine your registry" panel once the user has acted
   * (clicked a chip OR sent a typed follow-up). The question becomes stale the
   * moment a new turn starts, so we grey it out and drop the chips from the
   * persisted display log so a rehydrate doesn't bring them back as live.
   */
  #consumeRefinementPrompts() {
    const panels = this.#messageList.querySelectorAll('.birch-coherence--refine:not(.birch-coherence--answered)');
    if (panels.length === 0) return;
    panels.forEach((panel) => {
      panel.classList.add('birch-coherence--answered');
      panel.querySelectorAll('button').forEach((b) => {
        const btn = /** @type {HTMLButtonElement} */ (b);
        btn.disabled = true;
      });
    });
    // Forget the chips so the next page load won't re-render them as actionable.
    const before = this.#displayLog.length;
    this.#displayLog = this.#displayLog.filter((e) => e.kind !== 'refinement_prompts');
    if (this.#displayLog.length !== before) saveDraft(this.#sessionId, this.#displayLog);
  }

  /**
   * Guided "Walk Me Through" intake question — ONE warm question with clickable
   * chip answers, shown BEFORE the build. Reuses the `.birch-coherence` chip
   * styling (like the refinement panel) so it inherits existing theme CSS.
   *
   * Single-select: clicking a chip sends its label immediately as the next
   * user message. Multi-select: chips toggle on/off and a "Continue" button
   * sends all selected labels joined with ", " (so a family can pick BOTH a
   * crib AND a bassinet, etc.).
   * @param {HTMLElement} assistantEl
   * @param {Record<string, any>} data
   * @param {{ replay?: boolean }} [opts]
   */
  #renderIntakeQuestion(assistantEl, data, opts = {}) {
    const question = typeof data?.question === 'string' ? data.question.trim() : '';
    const options = Array.isArray(data?.options) ? data.options : [];
    const multiSelect = data?.multiSelect === true;
    const note = typeof data?.note === 'string' ? data.note.trim() : '';
    if (!question || options.length === 0) return;
    const panel = document.createElement('div');
    // `--intake` marks this so a subsequent send (chip/Continue click OR a typed
    // reply) can disable it — see #consumeIntakeQuestions.
    panel.className = 'birch-coherence birch-coherence--intake';
    let html =
      '<div class="birch-coherence__head">' +
        '<span class="birch-coherence__badge birch-coherence__badge--good">Let’s build together</span>' +
        '<span class="birch-coherence__summary">' + this.#escapeHtml(question) + '</span>' +
      '</div>';
    if (note) html += '<div class="birch-coherence__note">' + this.#escapeHtml(note) + '</div>';
    html += '<div class="birch-coherence__row"></div>';
    panel.innerHTML = html;
    const row = /** @type {HTMLElement} */ (panel.querySelector('.birch-coherence__row'));
    /** @type {Array<{ label: string }>} */
    const slimOptions = [];
    /** @type {Set<string>} */
    const selected = new Set();
    /** @type {HTMLButtonElement | null} */
    let continueBtn = null;
    for (const o of options.slice(0, 6)) {
      if (!o || typeof o.label !== 'string') continue;
      const label = String(o.label).trim();
      slimOptions.push({ label });
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'birch-coherence__chip birch-coherence__chip--add';
      btn.textContent = label;
      btn.addEventListener('click', () => {
        if (multiSelect) {
          // Toggle this pick; Continue stays disabled until ≥1 is selected.
          if (selected.has(label)) {
            selected.delete(label);
            btn.classList.remove('birch-coherence__chip--selected');
          } else {
            selected.add(label);
            btn.classList.add('birch-coherence__chip--selected');
          }
          if (continueBtn) continueBtn.disabled = selected.size === 0;
        } else {
          btn.classList.add('birch-coherence__chip--selected');
          this.#input.value = label;
          this.#send();
        }
      });
      row.appendChild(btn);
    }
    if (multiSelect) {
      continueBtn = document.createElement('button');
      continueBtn.type = 'button';
      continueBtn.className = 'birch-coherence__chip birch-coherence__chip--continue';
      continueBtn.textContent = 'Continue';
      continueBtn.disabled = true;
      continueBtn.addEventListener('click', () => {
        if (selected.size === 0) return;
        // Send the chosen labels in the order shown for a natural sentence.
        const picks = slimOptions.map((o) => o.label).filter((l) => selected.has(l));
        this.#input.value = picks.join(', ');
        this.#send();
      });
      row.appendChild(continueBtn);
    }
    assistantEl.appendChild(panel);
    this.#scrollMessagesToEnd();
    // Persist so a mid-intake page reload restores the chips. `opts.replay`
    // skips re-persisting when called from replayDisplayLog.
    if (!opts.replay) {
      this.#displayLog.push({
        kind: 'intake_question',
        data: { question, options: slimOptions, multiSelect, note },
      });
      saveDraft(this.#sessionId, this.#displayLog);
    }
  }

  /**
   * Disable any standing guided-intake question once the user acts (taps a
   * chip / Continue, or types a reply). Mirrors #consumeRefinementPrompts.
   */
  #consumeIntakeQuestions() {
    const panels = this.#messageList.querySelectorAll('.birch-coherence--intake:not(.birch-coherence--answered)');
    if (panels.length === 0) return;
    panels.forEach((panel) => {
      panel.classList.add('birch-coherence--answered');
      panel.querySelectorAll('button').forEach((b) => {
        const btn = /** @type {HTMLButtonElement} */ (b);
        btn.disabled = true;
      });
    });
    const before = this.#displayLog.length;
    this.#displayLog = this.#displayLog.filter((e) => e.kind !== 'intake_question');
    if (this.#displayLog.length !== before) saveDraft(this.#sessionId, this.#displayLog);
  }

  /**
   * Render a sticky in-chat "build progress" panel for registry:* events.
   * Single panel per assistant bubble; rows append as events arrive.
   * @param {HTMLElement} assistantEl
   * @param {string} eventType e.g. "registry:stage_started"
   * @param {Record<string, any>} data
   */
  /**
   * Post-build "optional upgrades" panel. The registry is already sound — these
   * are taste/lifestyle UPGRADES the user decides on, one per category, each with
   * its own reasoning sentence above a single action button. (Objective fixes are
   * auto-applied server-side and never reach here.) Clicking sends a natural
   * follow-up to the AI, which performs the swap/add and shows product cards.
   * @param {HTMLElement} assistantEl
   * @param {Record<string, any>} data
   * @param {{ replay?: boolean }} [opts]
   */
  #renderCoherenceReview(assistantEl, data, opts = {}) {
    const proposals = Array.isArray(data?.proposals) ? data.proposals : [];
    // Only render rows that carry both a reason and a label — a bare action with
    // no rationale is exactly the poor UX this panel was reworked to remove.
    const usable = proposals.filter((p) => p && typeof p.reason === 'string' && p.reason.trim()
      && typeof p.label === 'string' && p.label.trim());
    if (usable.length === 0) return;
    const panel = document.createElement('div');
    panel.className = 'birch-coherence birch-coherence--upgrades';
    const summary = String(data?.summary || 'A few optional upgrades you might consider:');
    panel.innerHTML =
      '<div class="birch-coherence__head">' +
        '<span class="birch-coherence__badge birch-coherence__badge--optional">Optional upgrades</span>' +
        '<span class="birch-coherence__summary">' + this.#escapeHtml(summary) + '</span>' +
      '</div>' +
      '<div class="birch-coherence__list"></div>';
    const list = /** @type {HTMLElement} */ (panel.querySelector('.birch-coherence__list'));
    // Trim to a slim shape before persisting so localStorage doesn't bloat and a
    // rehydrate has exactly what it needs to re-render the panel.
    const slimProposals = [];
    for (const p of usable.slice(0, 3)) {
      const type = String(p.type || 'swap');
      const reason = String(p.reason).trim();
      const label = String(p.label).trim();
      const itemTitle = p.item_title ? String(p.item_title) : '';
      const category = p.category ? String(p.category) : '';
      // The text the click sends — ALSO shown as the user's chat message, so keep
      // it a clean, natural directive (not a verbose meta-instruction). It must
      // read as a clear add/swap directive so the registry-builder skill completes
      // the mutation in ONE turn (its line-187 rule); the skill has the swap/add
      // tools + grounded registry contents to resolve the item and replacement.
      const query = type === 'add'
        ? `${label} for my registry, please.`
        : itemTitle
          ? `${label} — please swap out "${itemTitle}" for it, and pick the best match.`
          : `${label}, please.`;
      slimProposals.push({
        type, reason, label,
        ...(itemTitle && { item_title: itemTitle }),
        ...(category && { category }),
      });
      const item = document.createElement('div');
      item.className = 'birch-coherence__item';
      const reasonEl = document.createElement('p');
      reasonEl.className = 'birch-coherence__reason';
      reasonEl.textContent = reason;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'birch-coherence__chip birch-coherence__chip--' + this.#escapeHtml(type);
      btn.textContent = label;
      btn.addEventListener('click', () => {
        btn.classList.add('birch-coherence__chip--selected');
        this.#input.value = query;
        this.#send();
      });
      item.appendChild(reasonEl);
      item.appendChild(btn);
      list.appendChild(item);
    }
    assistantEl.appendChild(panel);
    this.#scrollMessagesToEnd();
    // Persist to the display log so a redirect-and-rehydrate (e.g.
    // build_complete → /pages/registry-edit) restores the panel on the next
    // page load. Like #renderRefinementPrompts, the coherence review is a
    // frontend-only SSE display entry — it never lands in the durable D1
    // transcript, so without this it silently vanishes on reload.
    // `opts.replay` skips re-persisting when called from a rehydrate/replay.
    if (!opts.replay && slimProposals.length > 0) {
      this.#displayLog.push({
        kind: 'coherence_review',
        data: { summary, proposals: slimProposals },
      });
      saveDraft(this.#sessionId, this.#displayLog);
    }
  }

  /**
   * Subtle "still working" indicator emitted as a heartbeat while Sonnet
   * reviews the registry. First tick creates the row; subsequent ticks just
   * pulse the spinner. Removed once coherence_review fires (or when the
   * stream ends without one — see the `done` handler).
   * @param {HTMLElement} assistantEl
   * @param {Record<string, any>} data
   */
  #renderCoherenceThinking(assistantEl, data) {
    let row = /** @type {HTMLElement | null} */ (assistantEl.querySelector('.birch-coherence-thinking'));
    if (!row) {
      row = document.createElement('div');
      row.className = 'birch-coherence-thinking';
      row.innerHTML =
        '<span class="birch-coherence-thinking__spinner" aria-hidden="true"></span>' +
        '<span class="birch-coherence-thinking__label">Reviewing your registry for fit…</span>';
      assistantEl.appendChild(row);
      this.#scrollMessagesToEnd();
    }
    // Pulse the spinner on each tick so the user can tell it's live.
    const tick = data && typeof data.tick === 'number' ? data.tick : 0;
    row.setAttribute('data-tick', String(tick));
  }

  /** @param {HTMLElement} assistantEl */
  #removeCoherenceThinking(assistantEl) {
    const row = assistantEl.querySelector('.birch-coherence-thinking');
    if (row && row.parentElement) row.parentElement.removeChild(row);
  }

  /**
   * @param {HTMLElement} assistantEl
   * @param {string} eventType
   * @param {Record<string, any>} data
   */
  #renderRegistryProgress(assistantEl, eventType, data) {
    let panel = /** @type {HTMLElement | null} */ (assistantEl.querySelector('.birch-build-calm'));
    // Only the full-build lifecycle (build_started / rebuild_started) creates
    // the panel. Refinement adds (single add_to_registry from a follow-up like
    // "add a breast pump") emit item_added events too — but those should be
    // confirmed in the AI's text reply, not by a persistent loader that has
    // no terminal event to clear it. If no panel exists for a non-build
    // event, skip the render.
    const isLifecycleStart = eventType === 'registry:build_started' || eventType === 'registry:rebuild_started';
    if (!panel && !isLifecycleStart) return;
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'birch-build-calm';
      panel.innerHTML =
        '<span class="birch-build-calm__orb" aria-hidden="true"></span>' +
        '<div class="birch-build-calm__body">' +
          '<p class="birch-build-calm__line" data-bp-line aria-live="polite">Getting started…</p>' +
          '<p class="birch-build-calm__sub" data-bp-sub></p>' +
        '</div>' +
        '<div class="birch-build-calm__track" data-bp-track aria-hidden="true"></div>';
      // Keep the build panel above the product rail. `.birch-cards` is nested
      // in `.birch-cards-wrap`, so anchor on that direct child of assistantEl.
      const rail = assistantEl.querySelector('.birch-cards-wrap');
      if (rail && rail.parentNode === assistantEl) assistantEl.insertBefore(panel, rail);
      else assistantEl.appendChild(panel);
    }

    switch (eventType) {
      case 'registry:rebuild_started':
      case 'registry:build_started': {
        panel.classList.remove('birch-build-calm--done', 'birch-build-calm--error');
        // Section progress pips, one per stage (the calm sense of "how far").
        const stages = data && Array.isArray(data.stages) ? data.stages : [];
        const track = /** @type {HTMLElement} */ (panel.querySelector('[data-bp-track]'));
        if (track) {
          track.innerHTML = '';
          for (const s of stages) {
            const pip = document.createElement('span');
            pip.className = 'birch-build-calm__pip';
            pip.dataset.stage = String(s && s.stage != null ? s.stage : '');
            track.appendChild(pip);
          }
        }
        // The personalized, calm line script the backend computed. Falls back
        // to a gentle generic loop when an older backend sends no script.
        const script = data && Array.isArray(data.script) && data.script.length
          ? data.script.map(String)
          : ['Getting your registry started…', 'Choosing trusted, parent-approved brands…', 'Putting your registry together with care…'];
        this.#startBuildCalm(panel, script);
        break;
      }
      case 'registry:stage_complete': {
        // Advance the progress track — light up this stage's pip. The narration
        // line keeps cross-fading independently so pacing stays calm.
        const stage = data && data.stage != null ? String(data.stage) : '';
        const pip = stage && panel.querySelector('.birch-build-calm__pip[data-stage="' + this.#cssEscape(stage) + '"]');
        if (pip) pip.classList.add('birch-build-calm__pip--done');
        break;
      }
      case 'registry:build_complete': {
        this.#stopBuildCalm();
        const total = data && typeof data.total === 'number' ? data.total : 0;
        const reviewBox = !!(data && data.review_box);
        panel.classList.add('birch-build-calm--done');
        const track = panel.querySelector('[data-bp-track]');
        if (track) track.querySelectorAll('.birch-build-calm__pip').forEach((p) => p.classList.add('birch-build-calm__pip--done'));
        const lineEl = /** @type {HTMLElement} */ (panel.querySelector('[data-bp-line]'));
        const subEl = /** @type {HTMLElement} */ (panel.querySelector('[data-bp-sub]'));
        if (lineEl) {
          lineEl.textContent = total <= 0
            ? 'All set for now.'
            : reviewBox
              ? 'Ready for you — ' + total + ' thoughtful suggestion' + (total === 1 ? '' : 's')
              : 'Your registry is ready — ' + total + ' thoughtful pick' + (total === 1 ? '' : 's');
        }
        if (subEl) subEl.textContent = reviewBox ? 'Review and keep the ones you love.' : 'Every pick chosen just for you.';
        // The way to the registry this build just filled — a reveal button when
        // it's the page behind the chat, a link to its edit page otherwise.
        const builtId = data && typeof data.registryId === 'string' ? data.registryId : '';
        if (total > 0 && builtId) this.#appendSeeRegistryCta(panel, builtId);
        // Guest build just finished — the single highest-intent moment to
        // mention an account. The registry IS saved (a real row owned by their
        // guest session, claimed automatically on sign-in), so the copy says
        // "keep it", never "or you'll lose it".
        if (total > 0 && this.#isGuestRegistrySession()) this.#appendGuestSaveNote(panel);
        break;
      }
      case 'registry:build_failed': {
        this.#stopBuildCalm();
        panel.classList.add('birch-build-calm--error');
        const lineEl = /** @type {HTMLElement} */ (panel.querySelector('[data-bp-line]'));
        const subEl = /** @type {HTMLElement} */ (panel.querySelector('[data-bp-sub]'));
        if (lineEl) lineEl.textContent = 'We hit a small snag.';
        if (subEl) subEl.textContent = 'Ask me to try again, or relax one preference and we’ll keep going.';
        break;
      }
      // item_added / stage_started / signal_extracted / gap_detected:
      // intentionally NOT rendered. The calm line script already speaks for the
      // build; streaming per-item rows or product names is the robotic feel we
      // are replacing. Gaps surface afterward in the AI's text summary.
      default:
        break;
    }
    this.#scrollMessagesToEnd();
  }

  /**
   * Begin (or restart) the calm cross-fade through the personalized line
   * script. One sentence at a time, a soft fade between, plus a slowly
   * rotating reassurance sub-line — paced so a busy mom can actually read it.
   * @param {HTMLElement} panel
   * @param {string[]} script
   */
  #startBuildCalm(panel, script) {
    this.#stopBuildCalm();
    this.#buildScript = script;
    this.#buildScriptIdx = 0;
    const lineEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bp-line]'));
    const subEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bp-sub]'));
    const reassurances = [
      'Only trusted, parent-approved brands.',
      'Every pick personalized to you.',
      'Quality and safety, double-checked.',
      'No clutter — just what you’ll really use.',
    ];

    const setLine = (/** @type {string} */ text) => {
      if (!lineEl) return;
      lineEl.classList.add('is-fading');
      window.setTimeout(() => {
        lineEl.textContent = text;
        lineEl.classList.remove('is-fading');
      }, 280);
    };

    // Show the first line immediately, then advance on a gentle cadence.
    if (lineEl && this.#buildScript.length) lineEl.textContent = this.#buildScript[0] ?? '';
    if (subEl) subEl.textContent = reassurances[0] ?? '';
    this.#buildScriptIdx = 1;

    this.#buildLineTimer = window.setInterval(() => {
      if (!this.#buildScript.length) return;
      // Cycle the curated lines; once exhausted, loop a couple of calm closers
      // so a long build never runs out of something soothing to say.
      const closers = ['Adding the finishing touches…', 'Almost there — making sure everything fits…'];
      const total = this.#buildScript.length;
      const idx = this.#buildScriptIdx;
      const text = idx < total ? this.#buildScript[idx] : closers[(idx - total) % closers.length];
      setLine(text ?? '');
      this.#buildScriptIdx = idx + 1;
    }, 2400);

    let ri = 1;
    this.#buildSubTimer = window.setInterval(() => {
      if (subEl) {
        subEl.classList.add('is-fading');
        window.setTimeout(() => {
          subEl.textContent = reassurances[ri % reassurances.length] ?? '';
          subEl.classList.remove('is-fading');
          ri += 1;
        }, 280);
      }
    }, 5200);
  }

  #stopBuildCalm() {
    if (this.#buildLineTimer != null) { clearInterval(this.#buildLineTimer); this.#buildLineTimer = null; }
    if (this.#buildSubTimer != null) { clearInterval(this.#buildSubTimer); this.#buildSubTimer = null; }
  }

  // Swap the send button between its two modes:
  //   stop  — solid square; shown while a registry build is streaming AND the
  //            input is empty (clicking aborts the build).
  //   send  — sparkle; shown at all other times (clicking sends/queues a message).
  #updateSendMode() {
    const btn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.birch-send'));
    if (!btn) return;
    const isStop = !!this.#buildingRegistryId && !this.#input.value.trim();
    if (isStop) {
      btn.setAttribute('aria-label', 'Stop build');
      btn.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">' +
        '<rect x="2" y="2" width="12" height="12" rx="2.5"/></svg>';
    } else {
      btn.setAttribute('aria-label', 'Send');
      btn.innerHTML =
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M12 2c.4 3.6 1.4 5.6 3 7.2 1.6 1.6 3.6 2.4 7 2.8-3.4.4-5.4 1.2-7 2.8-1.6 1.6-2.6 3.6-3 7.2-.4-3.6-1.4-5.6-3-7.2-1.6-1.6-3.6-2.4-7-2.8 3.4-.4 5.4-1.2 7-2.8 1.6-1.6 2.6-3.6 3-7.2Z" fill="currentColor"/>' +
        '<path d="M19 3c.2 1.4.5 2 1.1 2.6.6.6 1.2.9 2.6 1.1-1.4.2-2 .5-2.6 1.1-.6.6-.9 1.2-1.1 2.6-.2-1.4-.5-2-1.1-2.6-.6-.6-1.2-.9-2.6-1.1 1.4-.2 2-.5 2.6-1.1.6-.6.9-1.2 1.1-2.6Z" fill="currentColor" opacity=".85"/>' +
        '</svg>';
    }
  }

  async #abortBuild() {
    const registryId = this.#buildingRegistryId;
    // Clear state before aborting the fetch so #updateSendMode restores send icon.
    this.#buildingRegistryId = null;
    this.#pendingRegistryEditId = null;
    this.#pendingRegistryReveal = null;
    this.#queuedMessages = [];
    this.#updateSendMode();
    this.#stopBuildCalm();
    // Stamp the in-chat calm panel with a "stopped" state so it doesn't freeze
    // mid-animation. Reuses the --error visual but with user-initiated copy.
    const panel = /** @type {HTMLElement | null} */ (this.#messageList.querySelector('.birch-build-calm:not(.birch-build-calm--done):not(.birch-build-calm--error)'));
    if (panel) {
      panel.classList.add('birch-build-calm--error');
      const lineEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bp-line]'));
      const subEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bp-sub]'));
      if (lineEl) lineEl.textContent = 'Build stopped.';
      if (subEl) subEl.textContent = 'Edit your brief and try again.';
    }
    // Close the SSE stream — the finally block in #send() handles #sendInFlight.
    this.#abortController?.abort();
    // Notify the build canvas to reset to intake form.
    window.dispatchEvent(new CustomEvent('birch:registry:build_aborted', {
      detail: { registryId },
    }));
    // Tell the server to mark the registry as aborted and clear any partial items.
    if (registryId) {
      fetch('/apps/registry/api/registries/' + encodeURIComponent(registryId) + '/abort-build', {
        method: 'POST',
        credentials: 'same-origin',
      }).catch(() => {});
    }
  }

  /** @param {string} s */
  #cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  // ── Guest registry sessions ────────────────────────────────────────────────
  //
  // A signed-out shopper can run the Smart Registry builder: the registry is a
  // real, server-persisted row owned by an httpOnly guest-session cookie, and
  // signing in claims it. The widget's only job here is (a) making sure that
  // cookie exists before a registry-intent turn — otherwise the build tool has
  // no owner to write to — and (b) surfacing one honest save prompt at the end.

  /** True when this page is being viewed without a signed-in customer. */
  #isGuestRegistrySession() {
    // Our OWN `data-customer-id` decides this, and it is checked FIRST.
    // snippets/floating-ai-icon.liquid renders it on every page from Liquid's
    // `customer` object, so it is exactly as authoritative as the body marker
    // and — unlike the marker — cannot be missing on a surface someone forgot
    // to stamp. Deferring to BirchRegistry.isGuest() first is what made the AI
    // builder demand sign-in on PDPs and the non-builder registry pages: this
    // store is loaded there, its marker was not stamped there, and its
    // absent-marker fallback answers "customer" for a signed-out shopper.
    if (this.dataset.customerId) return false;
    if (document.body.dataset.regAuth) return document.body.dataset.regAuth === 'guest';
    const store = /** @type {any} */ (window).BirchRegistry;
    if (store && typeof store.isGuest === 'function') return store.isGuest();
    return true;
  }

  /**
   * Make sure a signed-out shopper has a guest session before a turn that may
   * touch the registry. Without it `build_registry_draft` has no owner and
   * refuses — and this is the ONLY mint point, so the widget asks the Registry
   * worker rather than minting anything itself.
   *
   * Awaited (not fired-and-forgotten) because the build tool runs inside the
   * very request we're about to send. It is a single memoized call per browser,
   * skipped entirely for signed-in customers.
   */
  async #ensureGuestRegistrySession() {
    if (!this.#isGuestRegistrySession()) return;
    const store = /** @type {any} */ (window).BirchRegistry;
    if (store && typeof store.ensureGuestSession === 'function') {
      await store.ensureGuestSession().catch(() => {});
      return;
    }
    // registry-store.js isn't on this page (e.g. a PDP) — mint directly. Same
    // endpoint, same idempotency; only the memoization is lost. The token comes
    // back in the BODY (Shopify's App Proxy strips Set-Cookie) and must be
    // stored under the same key registry-store.js uses, or the identity is lost
    // the moment the shopper navigates to a page that DOES load the store.
    await fetch('/apps/registry/api/guest/session', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (d && d.token) { try { localStorage.setItem('birch:grs', d.token); } catch (_) {} }
      try { localStorage.setItem('birch:guest_pending', '1'); } catch (_) {}
    }).catch(() => {});
  }

  /**
   * Guest identity for a direct /apps/ai/* call. The token lives in
   * localStorage because Shopify's App Proxy strips cookies both ways, so it has
   * to be replayed as `?gs=…` on every request or the worker sees an anonymous
   * caller and the registry tools refuse. Prefers registry-store.js's helper so
   * the transport is defined in exactly one place.
   */
  #withGuestToken(path) {
    const store = /** @type {any} */ (window).BirchRegistry;
    if (store && typeof store.withGuestToken === 'function') return store.withGuestToken(path);
    let token;
    try { token = localStorage.getItem('birch:grs'); } catch (_) { token = null; }
    if (!token) return path;
    return path + (path.indexOf('?') === -1 ? '?' : '&') + 'gs=' + encodeURIComponent(token);
  }

  /**
   * True when the current page IS the edit page for this registry — i.e. the
   * finished registry is already rendered on the page behind the chat, and the
   * only thing between the user and it is the panel itself.
   * @param {string} registryId
   */
  #isOnRegistryEditPage(registryId) {
    if (!/\/pages\/registry-edit\b/.test(location.pathname)) return false;
    try { return new URLSearchParams(location.search).get('id') === registryId; }
    catch { return false; }
  }

  /**
   * Uncover the registry sitting behind the chat, after a build finished on the
   * registry-edit page. Desktop: drop out of full screen so the panel returns to
   * its builder-page side column and "Currently in your registry" is visible
   * alongside the conversation. Mobile: there is no side-by-side, so the panel
   * is minimized instead — but only on an explicit tap, never automatically
   * (auto-closing a full-screen chat mid-read is disorienting).
   *
   * `auto: true` marks the post-build automatic call, which additionally
   * respects a full-screen state the USER chose: only a panel expanded on the
   * page's behalf (`#fullscreenAuto`) collapses itself.
   * @param {{ auto?: boolean }} [opts]
   */
  #revealRegistry(opts) {
    const auto = !!(opts && opts.auto);
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      if (auto) return;
      this.#toggle(false);
    } else if (this.#fullscreen && (!auto || this.#fullscreenAuto)) {
      this.#fullscreenAuto = false;
      this.#toggleFullscreen(false);
    }
    // Let the page bring its own "Currently in your registry" block into view —
    // the chat widget stays out of the registry page's DOM. registry-unified.js
    // handles this; nothing breaks on surfaces that don't listen.
    window.dispatchEvent(new CustomEvent('birch:registry:show_registry', {
      detail: { source: auto ? 'build_complete' : 'cta' },
    }));
  }

  /**
   * "See your registry" footer under a finished build. Always points at THE
   * registry this chat just built, but takes one of two forms:
   *
   *   on its edit page  — a button that uncovers the page behind the chat
   *                       (#revealRegistry). There is nowhere to navigate to;
   *                       the registry is already rendered, just hidden.
   *   anywhere else     — a link to that registry's edit page. The build also
   *                       auto-redirects there a moment later, but the link is
   *                       what makes the destination explicit (and what still
   *                       works if the user reads the summary first, or the
   *                       redirect is interrupted).
   *
   * Deduped so a re-render can't stack copies.
   * @param {HTMLElement} panel
   * @param {string} registryId
   */
  #appendSeeRegistryCta(panel, registryId) {
    if (panel.querySelector('.birch-build-done-cta')) return;
    const here = this.#isOnRegistryEditPage(registryId);
    const row = document.createElement('div');
    row.className = 'birch-build-done-cta';
    const text = document.createElement('p');
    text.className = 'birch-build-done-cta__text';
    // On the edit page the wording has to stay true whether the panel is still
    // covering the page or has already stepped aside into its column beside it.
    text.textContent = here
      ? 'Your picks are on the page — swap or add anything you like.'
      : 'Open it any time to swap, add, or set quantities.';
    row.appendChild(text);
    if (here) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'birch-build-done-cta__btn';
      btn.textContent = 'See your registry';
      btn.addEventListener('click', () => this.#revealRegistry());
      row.appendChild(btn);
    } else {
      // A plain in-chat link: the delegated handler in #attachEvents already
      // minimizes the full-screen panel before navigating on mobile, so this
      // needs no click handler of its own.
      const link = document.createElement('a');
      link.className = 'birch-build-done-cta__btn';
      link.href = '/pages/registry-edit?id=' + encodeURIComponent(registryId);
      link.textContent = 'See your registry';
      row.appendChild(link);
    }
    panel.appendChild(row);
  }

  /**
   * One-line "sign in to keep it" note under a finished guest build.
   *
   * Deliberately a note, not a blocking card: the registry already exists and
   * is fully editable, so this must read as an upgrade, not a paywall. Deduped
   * so a re-render can't stack copies.
   *
   * @param {HTMLElement} panel
   */
  #appendGuestSaveNote(panel) {
    if (panel.querySelector('.birch-guest-save')) return;
    const href =
      document.body.dataset.regSignin ||
      '/customer_authentication/login?return_to=' +
        encodeURIComponent(location.pathname + (location.search || ''));
    const note = document.createElement('div');
    note.className = 'birch-guest-save';
    note.innerHTML =
      '<p class="birch-guest-save__text">Your registry is saved. Sign in to keep it for good — and to share it and set delivery.</p>' +
      '<a class="birch-guest-save__cta" href="' + href.replace(/"/g, '&quot;') + '">Sign in to save</a>';
    panel.appendChild(note);
  }

  /** @param {HTMLElement} assistantEl @param {string[]} handles */
  #renderShowResultsBox(assistantEl, handles) {
    if (!SHOW_RESULTS_BOX_ENABLED) return;
    if (assistantEl.querySelector('.birch-show-results')) return; // dedupe
    const box = document.createElement('a');
    box.className = 'birch-show-results';
    box.href = this.#buildResultsUrl(handles);
    box.innerHTML = `
      <span class="birch-show-results__label">Open full page <span class="birch-show-results__count">(${handles.length})</span></span>
      <span class="birch-show-results__chev">›</span>
    `;
    // On MOBILE the panel is full-screen, so it must not cover the results page
    // this link opens. Minimize it on tap AND tag the destination URL with
    // chat=min so the companion stays minimized on arrival — persisting the open
    // state alone proved unreliable across the cross-document view transition.
    // Desktop: the panel is a small floating box, so leave it open (reopen
    // suppression is mobile-only, matching the history button).
    // Mobile minimize-before-nav is handled by the delegated link handler in
    // #attachEvents (covers this box, collection/page/product cards, and
    // markdown links uniformly), so no per-box click handler is needed here.
    assistantEl.appendChild(box);
    this.#scrollMessagesToEnd();
  }

  /**
   * Build the results-page URL for a specific search. The handle set is pinned
   * via `&h=` so each "Show results" box resolves to ITS OWN search rather than
   * the session's most-recent one (the search page walks the transcript and
   * takes the latest birch_search when `h` is absent). `session` is retained
   * for the header query and as a fallback for older links without `h`.
   * @param {string[]} handles
   */
  #buildResultsUrl(handles) {
    const url = new URL(SEARCH_RESULTS_PATH, window.location.origin);
    url.searchParams.set('view', 'ai');
    url.searchParams.set('session', this.#sessionId);
    // Cap keeps the URL well under browser limits; real searches return far
    // fewer than 100 handles.
    if (handles?.length) url.searchParams.set('h', handles.slice(0, 100).join(','));
    return url.toString();
  }

  /**
   * Update an already-rendered box as more cards stream in this turn: refresh
   * the count AND the pinned href. The box is created on the first card (when
   * only one handle is known), so the href must grow with the handle set —
   * otherwise the link would freeze pointing at just the first result.
   * @param {HTMLElement} assistantEl @param {string[]} handles
   */
  #updateShowResultsBox(assistantEl, handles) {
    const box = assistantEl.querySelector('.birch-show-results');
    if (!box) return;
    const c = box.querySelector('.birch-show-results__count');
    if (c) c.textContent = `(${handles.length})`;
    box.setAttribute('href', this.#buildResultsUrl(handles));
  }

  /**
   * @param {string} role
   * @param {string} text
   * @returns {HTMLElement}
   */
  #appendMessage(role, text) {
    // Any real message means the conversation is no longer empty — retire the
    // contextual starter pills (and the transcript skeleton, if the user typed
    // before a past chat finished loading) the instant content lands.
    this.#clearEmptyState();
    this.#clearTranscriptLoading();
    const div = document.createElement('div');
    div.className = `birch-msg birch-msg--${role}`;
    // Assistant text gets markdown rendering (bold, lists, inline code, etc.).
    // User messages stay as escaped plain text — markdown there could surprise
    // people who type "*" or "#" without meaning formatting.
    const body = role === 'assistant'
      ? this.#renderMarkdown(text)
      : this.#escapeHtml(text);
    div.innerHTML = `<div class="birch-msg__text">${body}</div>`;
    this.#messageList.appendChild(div);
    this.#scrollMessagesToEnd();
    return div;
  }

  #scrollMessagesToEnd() {
    const list = this.#messageList;
    const end = list.scrollHeight - list.clientHeight;
    const anchor = this.#responseAnchor;
    if (!anchor?.isConnected) {
      list.scrollTop = end;
      return;
    }
    // Offset of the turn's user bubble within the scrolled content, less a
    // little breathing room so it doesn't sit flush against the top edge.
    const anchorTop = list.scrollTop
      + anchor.getBoundingClientRect().top - list.getBoundingClientRect().top - 12;
    list.scrollTop = Math.max(0, Math.min(end, anchorTop));
  }

  /**
   * True when the chat is operating in a registry context (building/editing a
   * registry, or live on the registry-edit page). TrustScore is a registry-
   * grade signal, so non-Birch (external web-result) cards only surface it here;
   * in ordinary product chat the score is hidden for non-Birch products.
   */
  #isRegistryView() {
    return Boolean(
      this.#pendingRegistryId ||
      /** @type {any} */ (window).__birchActiveRegistryId ||
      this.#pendingIntent === 'registry_builder' ||
      this.#registryBuildActive ||
      /\/pages\/registry-edit\b/.test(location.pathname)
    );
  }

  /**
   * Render a LushLife (external) product card inline in the chat.
   * @param {HTMLElement} container
   * @param {Record<string, any>} card
   */
  #renderExternalCard(container, card) {
    const a = document.createElement('a');
    a.className = 'birch-card birch-card--external';
    // Web-search result — link DIRECTLY to the retailer (never the partner
    // catalog page). primaryLink is guaranteed by the worker's usable-card gate;
    // fall back to lushlifeUrl only if an older persisted card lacks it.
    a.href = card.primaryLink || card.lushlifeUrl || `https://lushlife.ai/product/${encodeURIComponent(card.handle)}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    // Badge makes it clear this came from a web search (not the Birch catalog).
    const badgeLabel = card.primaryRetailer ? `Web · ${card.primaryRetailer}` : 'Web result';
    // TrustScore only shows for non-Birch products inside a registry view.
    const showScore = card.trustScore && this.#isRegistryView();
    a.innerHTML = `
      <span class="birch-card__badge">${this.#escapeHtml(badgeLabel)}</span>
      <img class="birch-card__img" src="${this.#escapeHtml(card.image)}" alt="${this.#escapeHtml(card.title)}" loading="lazy" width="120" height="120">
      <div class="birch-card__body">
        <p class="birch-card__title">${this.#escapeHtml(card.title)}</p>
        ${card.brand ? `<p class="birch-card__brand">${this.#escapeHtml(card.brand)}</p>` : ''}
        <p class="birch-card__price">${card.priceFrom ? 'From ' : ''}$${(Number(card.price) || 0).toFixed(2)}</p>
        ${showScore ? `<span class="birch-card__score" style="--trust-score-value: ${Number(card.trustScore) || 0};">TrustScore: ${this.#escapeHtml(String(card.trustScore))}</span>` : ''}
      </div>
    `;
    container.appendChild(a);
    this.#scrollMessagesToEnd();
  }

  /**
   * Render a Birch (on-site Shopify) product card inline in the chat — same
   * visual treatment as LushLife cards, but the link points at the on-site
   * product page and opens in the same tab (it's an internal page).
   * @param {HTMLElement} container
   * @param {Record<string, any>} card
   */
  #renderBirchCard(container, card) {
    const a = document.createElement('a');
    a.className = 'birch-card';
    a.href = this.#productUrl(String(card.handle));
    const queryId = typeof card.junoQueryId === 'string' ? card.junoQueryId : '';
    const position = Number.isInteger(card.junoPosition) && card.junoPosition > 0 ? card.junoPosition : 0;
    if (queryId && position && card.handle) {
      a.addEventListener('click', () => {
        const interactionId = newSessionId();
        // Store only the opaque join ids needed after the product page loads;
        // Shopify consent is checked again before either request is sent.
        try {
          sessionStorage.setItem(JUNO_DWELL_KEY, JSON.stringify({
            queryId,
            handle: String(card.handle),
            position,
            interactionId,
          }));
        } catch { /* product views remain usable when storage is unavailable */ }
        this.#sendJunoInteraction({
          kind: 'view',
          query_id: queryId,
          handle: String(card.handle),
          position,
          interaction_id: interactionId,
        });
      });
    }
    a.innerHTML = `
      <img class="birch-card__img" src="${this.#escapeHtml(card.image)}" alt="${this.#escapeHtml(card.title)}" loading="lazy" width="120" height="120">
      <div class="birch-card__body">
        <p class="birch-card__title">${this.#escapeHtml(card.title)}</p>
        ${card.brand ? `<p class="birch-card__brand">${this.#escapeHtml(card.brand)}</p>` : ''}
        <p class="birch-card__price">${card.priceFrom ? 'From ' : ''}$${(Number(card.price) || 0).toFixed(2)}</p>
        ${card.trustScore ? `<span class="birch-card__score" style="--trust-score-value: ${Number(card.trustScore) || 0};">TrustScore: ${this.#escapeHtml(String(card.trustScore))}</span>` : ''}
      </div>
    `;
    container.appendChild(a);
    this.#scrollMessagesToEnd();
  }

  /**
   * Slim a Birch product card down to what we persist in the draft display log
   * (mirrors #slimExternalCard) so it can re-render inline after a reload.
   * @param {Record<string, any>} card
   */
  #slimBirchCard(card) {
    return {
      handle: card.handle,
      title: card.title,
      brand: card.brand,
      image: card.image,
      price: card.price,
      priceFrom: card.priceFrom,
      compareAtPrice: card.compareAtPrice,
      trustScore: card.trustScore,
      junoQueryId: card.junoQueryId,
      junoPosition: card.junoPosition,
    };
  }

  /** Send a non-blocking interaction through the signed Ask Birch App Proxy.
   * The page never receives the Juno Engine URL or a customer identity. */
  #sendJunoInteraction(interaction) {
    const analytics = junoAnalyticsContext();
    if (analytics.juno_analytics_allowed !== true || !analytics.juno_visitor_id) return;
    const url = this.#withGuestToken(`${PROXY}/juno/interaction`);
    const body = JSON.stringify({
      ...interaction,
      session_id: this.#sessionId,
      ...analytics,
    });

    // pagehide is often too late for a regular fetch to leave the document.
    // Beacon hands the small, same-origin payload to the browser's unload queue;
    // the Edge route accepts this exact JSON body. Views continue to use fetch
    // so their request is observable and debuggable during normal navigation.
    if (interaction.kind === 'dwell' && typeof navigator.sendBeacon === 'function') {
      try {
        if (navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
      } catch { /* Fall through to the keepalive fetch. */ }
    }

    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body,
    }).catch(() => {});
  }

  /** Notify the signed edge route of an explicit consent withdrawal. It derives
   * the customer identity from the App Proxy signature, not from this browser. */
  #syncJunoConsentWithdrawal() {
    const analytics = junoAnalyticsContext();
    if (analytics.juno_analytics_allowed !== false) return;
    void fetch(this.#withGuestToken(`${PROXY}/juno/interaction`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify(analytics),
    }).catch(() => {});
  }

  /** Pick up the one card opened just before this PDP navigation and report
   * visible time only. Hidden-tab time is deliberately excluded. */
  #startJunoProductDwell() {
    const analytics = junoAnalyticsContext();
    if (analytics.juno_analytics_allowed === false) {
      try { sessionStorage.removeItem(JUNO_DWELL_KEY); } catch { /* ignore */ }
      return;
    }
    if (analytics.juno_analytics_allowed !== true) return;

    let pending;
    try {
      const raw = sessionStorage.getItem(JUNO_DWELL_KEY);
      if (!raw) return;
      pending = JSON.parse(raw);
      sessionStorage.removeItem(JUNO_DWELL_KEY);
    } catch { return; }
    if (!pending || typeof pending !== 'object') return;
    const queryId = typeof pending.queryId === 'string' ? pending.queryId : '';
    const handle = typeof pending.handle === 'string' ? pending.handle : '';
    const interactionId = typeof pending.interactionId === 'string' ? pending.interactionId : '';
    if (!queryId || !handle || !interactionId) return;
    try {
      if (new URL(this.#productUrl(handle), location.origin).pathname !== location.pathname) return;
    } catch { return; }

    let activeStartedAt = document.visibilityState === 'visible' ? Date.now() : 0;
    let visibleMs = 0;
    let sent = false;
    const pause = () => {
      if (activeStartedAt) visibleMs += Date.now() - activeStartedAt;
      activeStartedAt = 0;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !activeStartedAt) activeStartedAt = Date.now();
      else if (document.visibilityState !== 'visible') pause();
    };
    const finish = () => {
      if (sent) return;
      pause();
      sent = true;
      if (visibleMs > 0) this.#sendJunoInteraction({
        kind: 'dwell', query_id: queryId, handle, interaction_id: interactionId, dwell_ms: visibleMs,
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', finish, { once: true });
  }

  /**
   * Whether the user is already on the page `url` points at — in which case we
   * suppress the link (no point offering "Browse all diapers" while standing in
   * the diapers collection). Checks the live page context first (authoritative
   * resolved handle), then falls back to the pathname so locale-prefixed URLs
   * (/en-us/collections/…) still match.
   * @param {string} url storefront-relative path, e.g. "/collections/diapers"
   * @returns {boolean}
   */
  /**
   * True when the chat is open on a tracker / log page. Reads the live URL as
   * well as the injected context, because the widget survives navigation while
   * window.BirchAI.context is written once per page load.
   * @returns {boolean}
   */
  #onTrackerSurface() {
    try {
      const ctx = window.BirchAI?.context;
      if (ctx?.pageHandle && TRACKER_PAGE_HANDLES.has(String(ctx.pageHandle).toLowerCase())) return true;
      const m = (location.pathname || '').toLowerCase().match(/^\/pages\/([^/]+)\/?$/);
      return !!(m && TRACKER_PAGE_HANDLES.has(m[1]));
    } catch { return false; }
  }

  #isAlreadyOnPage(url) {
    try {
      const target = String(url || '').replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
      if (!target) return false;
      const ctx = window.BirchAI?.context;
      const coll = target.match(/^\/collections\/([^/]+)$/);
      if (coll && ctx?.collection?.handle && String(ctx.collection.handle).toLowerCase() === coll[1]) return true;
      const page = target.match(/^\/pages\/([^/]+)$/);
      if (page && ctx?.pageHandle && String(ctx.pageHandle).toLowerCase() === page[1]) return true;
      const cur = (location.pathname || '').replace(/\/+$/, '').toLowerCase();
      return cur === target || cur.endsWith(target);
    } catch { return false; }
  }

  /**
   * Site-navigation button. The worker validated `url` against the live page
   * catalog, so it always points at a real on-site page. Same-tab nav (no
   * target=_blank) — it's an internal page, and the user opted into being taken
   * there. The user taps it; the chat is preserved until they do.
   *
   * Buttons live in one flex `.birch-nav-row` appended to the assistant bubble
   * BELOW the (horizontally-scrolling) `.birch-cards`, so a related collection
   * link and an LLM-offered page link sit side by side under the cards rather
   * than off to the side inside the scroller. Returns false (and renders
   * nothing) when the user is already on the target page, so callers can skip
   * logging it for replay.
   * @param {HTMLElement} assistantEl the assistant message bubble
   * @param {Record<string, any>} data
   * @param {{ skipPageCheck?: boolean }} [opts]
   * @returns {boolean} whether a button was rendered
   */
  #renderNavigateButton(assistantEl, data, opts = {}) {
    const url = String(data.url ?? '');
    if (!url) return false;
    // Live render: don't offer a link to the page you're already on. Replay/
    // rehydrate (skipPageCheck): the button is a historical part of the
    // transcript, so keep it even after the user navigated to that page —
    // otherwise tapping a "go to X" button makes the button vanish on X.
    if (!opts.skipPageCheck && this.#isAlreadyOnPage(url)) return false;
    let row = assistantEl.querySelector(':scope > .birch-nav-row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'birch-nav-row';
      assistantEl.appendChild(row);
    }
    // Skip a duplicate link to the same page within the same turn.
    if (row.querySelector(`a[data-url="${CSS.escape(url)}"]`)) return false;
    const label = String(data.label || data.title || 'Open page');
    const a = document.createElement('a');
    a.dataset.url = url;
    a.className = 'birch-nav-card';
    a.href = url;
    a.innerHTML = `
      <span class="birch-nav-card__label">${this.#escapeHtml(label)}</span>
      <span class="birch-nav-card__arrow" aria-hidden="true">›</span>
    `;
    row.appendChild(a);
    this.#scrollMessagesToEnd();
    return true;
  }

  /** Strip a LushLife card to the minimum needed for replay. */
  /** @param {Record<string, any>} card */
  #slimExternalCard(card) {
    return {
      handle: card.handle,
      title: card.title,
      brand: card.brand,
      image: card.image,
      price: card.price,
      trustScore: card.trustScore,
      // Keep the retailer link + name so a replayed web-search card still links
      // straight to the retailer (and shows the right badge).
      primaryLink: card.primaryLink,
      primaryRetailer: card.primaryRetailer,
      lushlifeUrl: card.lushlifeUrl,
    };
  }

  /**
   * Render disambiguation pills (Slice 7). Sits inside the assistant's bubble
   * so they read as part of the AI's reply. Click → fills the input with the
   * pill's `query` and sends as a continuation turn.
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>} data
   */
  #renderDisambiguationPills(parentEl, data) {
    const wrap = document.createElement('div');
    wrap.className = 'birch-pills';

    if (data.prompt_text) {
      const p = document.createElement('p');
      p.className = 'birch-pills__prompt';
      p.textContent = String(data.prompt_text);
      wrap.appendChild(p);
    }

    const row = document.createElement('div');
    row.className = 'birch-pills__row';
    const pills = Array.isArray(data.pills) ? data.pills : [];
    for (const pill of pills) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'birch-pill';
      const label = String(pill.label ?? pill.query ?? '');
      const count = typeof pill.count === 'number' && pill.count > 0 ? ` (${pill.count})` : '';
      btn.textContent = label + count;
      btn.addEventListener('click', () => {
        const q = String(pill.query ?? pill.label ?? '');
        if (!q) return;
        // Once any pill in this group is clicked, the chip set has served
        // its purpose — disable + dim the whole row so the answered prompt
        // doesn't sit beneath the user's reply forever. We keep the wrap in
        // the DOM (so the persisted display log still reflects what was
        // shown) but visually mark it as resolved.
        this.#markPillsAnswered(wrap, label);
        this.#input.value = q;
        // Pill click is a continuation turn (per refined §3 — selection-style
        // entries continue the chat). Send goes through normal #send() so it
        // hits the same orchestration as a typed message.
        this.#send();
      });
      row.appendChild(btn);
    }
    wrap.appendChild(row);
    parentEl.appendChild(wrap);
    this.#scrollMessagesToEnd();
  }

  /**
   * Render the single "Search the web" button shown under a no-results message
   * (non-registry). Tapping it re-asks the same query with the one-shot
   * webSearch flag so the worker runs its web fallback and streams retailer-
   * linked cards back. Mirrors the disambiguation-pill click pattern.
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>} data
   */
  #renderWebSearchPill(parentEl, data) {
    const query = String(data.query ?? '').trim();
    const wrap = document.createElement('div');
    wrap.className = 'birch-pills birch-pills--websearch';

    const row = document.createElement('div');
    row.className = 'birch-pills__row';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'birch-pill birch-pill--websearch';
    btn.textContent = query ? `Find external products for “${query}”` : 'Find external products 🔍';
    btn.addEventListener('click', () => {
      this.#markPillsAnswered(wrap, btn.textContent || '');
      // One-shot: the next #send() attaches webSearch:true, then clears it.
      this.#pendingWebSearch = true;
      this.#input.value = query ? `Find external products for ${query}` : 'Find external products';
      this.#send();
    });
    row.appendChild(btn);
    wrap.appendChild(row);
    parentEl.appendChild(wrap);
    this.#scrollMessagesToEnd();
  }

  /**
   * Disable a clickable-pill group after one of its pills has been chosen,
   * and tag the chosen pill so the user can see what they picked. Keeps the
   * group in the DOM (the next chat-turn's draft replay relies on the
   * displayLog being faithful to what the user saw) but makes it visually
   * resolved.
   * @param {HTMLElement} wrap
   * @param {string} chosenLabel
   */
  #markPillsAnswered(wrap, chosenLabel) {
    if (!wrap || wrap.classList.contains('birch-pills--answered')) return;
    wrap.classList.add('birch-pills--answered');
    const buttons = wrap.querySelectorAll('button.birch-pill');
    buttons.forEach((b) => {
      const btn = /** @type {HTMLButtonElement} */ (b);
      btn.disabled = true;
      // Mark only the chosen pill so the user can see what they picked.
      if (btn.textContent && btn.textContent.trim() === chosenLabel.trim()) {
        btn.classList.add('birch-pill--chosen');
      }
    });
  }

  /** Slim pills for the draft replay log so a refresh restores them. */
  /** @param {Record<string, any>} data */
  #slimPills(data) {
    return {
      prompt_text: data.prompt_text,
      kind: data.kind,
      pills: Array.isArray(data.pills) ? data.pills.map((/** @type {any} */ p) => ({
        label: p.label, query: p.query, count: p.count, source: p.source,
      })) : [],
    };
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>[]} orders
   */
  /** Translate Shopify fulfillment status codes into friendly labels.
   * @param {unknown} raw
   * @returns {string}
   */
  #orderStatusLabel(raw) {
    /** @type {Record<string, string>} */
    const map = {
      FULFILLED:            'Shipped',
      PARTIALLY_FULFILLED:  'Partially shipped',
      UNFULFILLED:          'Not yet shipped',
      IN_TRANSIT:           'In transit',
      OUT_FOR_DELIVERY:     'Out for delivery',
      DELIVERED:            'Delivered',
      ON_HOLD:              'On hold',
      SCHEDULED:            'Scheduled',
      ATTEMPTED_DELIVERY:   'Delivery attempted',
      READY_FOR_PICKUP:     'Ready for pickup',
      FAILURE:              'Delivery issue',
    };
    return map[String(raw).toUpperCase()] ?? String(raw).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>[]} orders
   */
  #renderOrderList(parentEl, orders) {
    const list = document.createElement('div');
    list.className = 'birch-orders';

    list.innerHTML = orders.map((o) => {
      // Cancelled/delivered outrank fulfillmentStatus: a cancelled order stays
      // UNFULFILLED in Shopify, so mapping status alone showed it as "Not yet
      // shipped"; likewise a delivered one only ever reached "Shipped".
      const statusOverride = o.cancelledAt ? 'CANCELLED' : (o.deliveredAt ? 'DELIVERED' : null);
      const statusRaw = statusOverride ?? o.fulfillmentStatus ?? o.status ?? '';
      const statusLabel = statusOverride === 'CANCELLED' ? 'Cancelled' : this.#orderStatusLabel(statusRaw);
      const statusSlug  = String(statusRaw).toLowerCase().replace(/_/g, '-');
      const dateStr = o.createdAt
        ? new Date(o.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';
      const items = (/** @type {Record<string, any>[]} */ (o.lineItems ?? [])).slice(0, 3)
        .map((li) => `<span class="birch-order__item">${this.#escapeHtml(li.title)} &times;${li.quantity}</span>`)
        .join('');
      const tracking = o.trackingUrl
        ? `<a class="birch-order__track" href="${this.#escapeHtml(o.trackingUrl)}" target="_blank" rel="noopener">Track shipment &rarr;</a>`
        : '';
      // The order's own page — the same URL the orders list links to
      // (sections/page-orders.liquid): /pages/order-detail?id=<numeric id>.
      const numericId = (String(o.id ?? '').match(/(\d+)$/) || [])[1];
      const details = numericId
        ? `<a class="birch-order__details" href="/pages/order-detail?id=${numericId}" data-order-details-link>View order details &rarr;</a>`
        : '';
      const links = details || tracking ? `<div class="birch-order__links">${details}${tracking}</div>` : '';

      return `
        <div class="birch-order${details ? ' birch-order--link' : ''}">
          <div class="birch-order__head">
            <span class="birch-order__num">${this.#escapeHtml(o.orderNumber)}</span>
            <span class="birch-order__status birch-order__status--${statusSlug}">${this.#escapeHtml(statusLabel)}</span>
            <span class="birch-order__total">$${(o.total ?? 0).toFixed(2)}</span>
          </div>
          ${dateStr ? `<div class="birch-order__date">Placed ${this.#escapeHtml(dateStr)}</div>` : ''}
          <div class="birch-order__items">${items}</div>
          ${links}
        </div>
      `;
    }).join('');

    // A click anywhere on a card opens the order, like the orders list
    // (assets/page-orders.js setupCardNavigation). The "View order details"
    // link stays the keyboard and screen-reader path; links inside the card
    // (tracking) handle their own clicks.
    list.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.closest('a, button')) return;
      const card = target.closest('.birch-order');
      const link = /** @type {HTMLAnchorElement | null} */ (card && card.querySelector('[data-order-details-link]'));
      if (!link) return;
      // Don't hijack the mouse-up that ends a text selection inside the card.
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && card && card.contains(selection.anchorNode)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        window.open(link.href, '_blank', 'noopener');
        return;
      }
      window.location.assign(link.href);
    });

    // "View all orders" link below the cards
    const footer = document.createElement('a');
    footer.className = 'birch-orders__footer-link';
    footer.href = '/pages/orders';
    footer.textContent = 'View all orders →';
    list.appendChild(footer);

    parentEl.appendChild(list);
    this.#scrollMessagesToEnd();
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>} order
   */
  #renderOrderDetails(parentEl, order) {
    if (!order) return;
    this.#renderOrderList(parentEl, [order]);
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>} card
   */
  #renderConfirmationCard(parentEl, card) {
    const wrap = document.createElement('div');
    wrap.className = 'birch-confirm';
    const isCancel = card.action === 'cancel_order';
    // Name the items being returned. "Return #1017 — 2 items" alone asks the
    // customer to approve a mutation without telling them what it covers,
    // which matters most on partially-fulfilled orders where only some lines
    // are returnable.
    const cardItems = /** @type {Record<string, any>[]} */ (card.lineItems ?? []);
    const itemsHtml = isCancel || cardItems.length === 0
      ? ''
      : `<ul class="birch-confirm__items">${cardItems.map((l) => {
          const variant = l.variantTitle ? ` <span class="birch-confirm__variant">${this.#escapeHtml(String(l.variantTitle))}</span>` : '';
          const qty = Number(l.quantity) > 1 ? ` ×${Number(l.quantity)}` : '';
          return `<li>${this.#escapeHtml(String(l.title ?? ''))}${variant}${qty}</li>`;
        }).join('')}</ul>`;
    // The return questions' answers (condition, exchange choice) — part of what
    // the customer is approving, so they sit on the card. The worker binds the
    // same answers into the confirmation token.
    const cardDetails = /** @type {unknown[]} */ (Array.isArray(card.details) ? card.details : []);
    const detailsHtml = isCancel || cardDetails.length === 0
      ? ''
      : `<ul class="birch-confirm__details">${cardDetails.map((d) => `<li>${this.#escapeHtml(String(d))}</li>`).join('')}</ul>`;
    // "(required)" in a placeholder enforces nothing. The worker sends
    // `reasonRequired`; fall back to reading the placeholder so a theme
    // deployed ahead of the worker still gates correctly.
    const reasonRequired = typeof card.reasonRequired === 'boolean'
      ? card.reasonRequired
      : /\(required\)/i.test(String(card.reasonPlaceholder || ''));
    const isExchange = !isCancel && card.resolution === 'exchange';
    // Keep it for Birch Credit — no return at all.
    const isKeep = !isCancel && card.resolution === 'keep';
    // "Make it right" — the support team fixes it; no return, no refund.
    const isSupport = !isCancel && card.resolution === 'support';
    wrap.innerHTML = `
      <div class="birch-confirm__title">${isCancel ? 'Cancel order?' : isSupport ? 'Ask our team to make it right?' : isKeep ? 'Keep it for Birch Credit?' : isExchange ? 'Start exchange?' : 'Start return?'}</div>
      <div class="birch-confirm__summary">${this.#escapeHtml(card.summary)}</div>
      ${itemsHtml}
      ${detailsHtml}
      <textarea class="birch-confirm__reason" placeholder="${this.#escapeHtml(card.reasonPlaceholder)}" maxlength="500"${reasonRequired ? ' required aria-required="true"' : ''}></textarea>
      <div class="birch-confirm__actions">
        <button class="birch-confirm__cancel" type="button">Never mind</button>
        <button class="birch-confirm__ok" type="button"${reasonRequired ? ' disabled aria-disabled="true"' : ''}>${isCancel ? 'Cancel order' : isSupport ? 'Send to our team' : isKeep ? 'Get credit' : isExchange ? 'Submit exchange' : 'Submit return'}</button>
      </div>
    `;
    parentEl.appendChild(wrap);

    const reasonEl = /** @type {HTMLTextAreaElement} */ (wrap.querySelector('.birch-confirm__reason'));
    const okBtn = /** @type {HTMLButtonElement} */ (wrap.querySelector('.birch-confirm__ok'));
    if (reasonRequired) {
      // Whitespace is not a reason. Re-evaluated on every edit, including
      // paste, autofill and IME composition, which `input` alone covers.
      const syncOk = () => {
        const ok = reasonEl.value.trim().length > 0;
        okBtn.disabled = !ok;
        okBtn.setAttribute('aria-disabled', String(!ok));
        okBtn.title = ok ? '' : 'Tell us why to continue';
      };
      reasonEl.addEventListener('input', syncOk);
      reasonEl.addEventListener('change', syncOk);
      syncOk();
    }

    /** @type {HTMLElement} */ (wrap.querySelector('.birch-confirm__cancel')).addEventListener('click', () => wrap.remove());
    okBtn.addEventListener('click', () => {
      const reason = reasonEl.value.trim();
      // Belt and braces: a disabled button shouldn't fire, but a stray
      // programmatic click must not submit a return with no reason either.
      if (reasonRequired && reason === '') {
        reasonEl.focus();
        return;
      }
      // The token rides the request body (confirmationToken), NOT the visible
      // message — the worker injects it into the confirmed tool call. The
      // message itself stays human: it's rendered as the user's own bubble
      // and persisted to the transcript.
      //
      // That's also why the line items are named by TITLE here and not by
      // `gid://shopify/LineItem/…`: the ids are bound into the signed token at
      // mint time, so the worker already knows exactly which items the user
      // approved. Echoing raw ids into the bubble only ever showed the
      // customer database plumbing.
      this.#pendingConfirmation = String(card.confirmationToken || '');
      const reasonPart = reason ? ` Reason: ${reason}` : '';
      const titles = (/** @type {Record<string, any>[]} */ (card.lineItems ?? []))
        .map((l) => String(l.title || '').trim())
        .filter(Boolean);
      const itemsPart = isCancel || titles.length === 0 ? '' : ` Items: ${titles.join(', ')}.`;
      this.#input.value = isCancel
        ? `Yes, cancel order ${card.orderNumber} — confirmed via the confirmation card.${reasonPart}`
        : isSupport
          ? `Yes, send the items from ${card.orderNumber} to your team to make right — confirmed via the confirmation card.${itemsPart}${reasonPart}`
        : isKeep
          ? `Yes, I'll keep the items from ${card.orderNumber} for the Birch Credit — confirmed via the confirmation card.${itemsPart}${reasonPart}`
          : `Yes, submit the ${isExchange ? 'exchange' : 'return'} for ${card.orderNumber} — confirmed via the confirmation card.${itemsPart}${reasonPart}`;
      wrap.remove();
      this.#send();
    });
    this.#scrollMessagesToEnd();
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {string} text
   */
  #renderReceipt(parentEl, text) {
    const div = document.createElement('div');
    div.className = 'birch-receipt';
    div.textContent = text;
    parentEl.appendChild(div);
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>} data
   */
  #renderOfflineFallback(parentEl, data) {
    const div = document.createElement('div');
    div.className = 'birch-offline';
    const email = this.#escapeHtml(data.email || 'support@birchstore.com');
    div.innerHTML = `
      <p>You can reach our team any time at <a href="mailto:${email}">${email}</a>.</p>
      <p>Or just drop your message here and we'll email you back — usually within 24 hours.</p>
      ${data.nextWindow ? `<p class="birch-offline__hours">Live support hours: ${this.#escapeHtml(data.nextWindow)}</p>` : ''}
    `;
    parentEl.appendChild(div);
  }

  /**
   * @param {string} mode
   * @param {number} [etaSeconds]
   */
  #enterHandoffMode(mode, etaSeconds) {
    this.#mode = mode;
    persistSession(this.#sessionId, mode, this.#lastSeq);
    if (!this.#handoffBanner) {
      this.#handoffBanner = document.createElement('div');
      this.#handoffBanner.className = 'birch-handoff-banner';
      this.#panel.querySelector('.birch-panel__header')?.after(this.#handoffBanner);
    }
    const statusText = mode === 'human'
      ? "You're now chatting with a human agent."
      : `Connecting you with a human agent… (avg ${Math.round((etaSeconds ?? 120) / 60)} min)`;
    this.#handoffBanner.innerHTML = '';
    const span = document.createElement('span');
    span.textContent = statusText;
    const endBtn = document.createElement('button');
    endBtn.type = 'button';
    endBtn.className = 'birch-handoff-end';
    endBtn.textContent = 'End chat';
    endBtn.addEventListener('click', () => this.#userEndHandoff());
    this.#handoffBanner.appendChild(span);
    this.#handoffBanner.appendChild(endBtn);
    // Visual cue that further messages go to the agent (still allowed — the
    // server bypasses the LLM and routes them, item #11).
    this.#input.placeholder = mode === 'human' ? 'Message the agent…' : 'Message the support team…';
    this.#startStream();
  }

  async #userEndHandoff() {
    try {
      await fetch(`${PROXY}/handoff/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: this.#sessionId }),
      });
    } catch { /* best effort */ }
    this.#exitHandoffMode('user_ended');
  }

  /** @param {string} reason */
  #exitHandoffMode(reason) {
    this.#mode = 'ai';
    this.#stopStream();

    const msg = reason === 'idle_timeout'
      ? "Session ended due to inactivity. Starting a new conversation…"
      : reason === 'user_ended'
      ? "Chat ended. Starting a new conversation…"
      : "Agent ended the chat. Starting a new conversation…";

    if (this.#handoffBanner) {
      this.#handoffBanner.innerHTML = '';
      this.#handoffBanner.textContent = msg;
    }

    this.#updatePlaceholder();

    // After a short delay: new session ID + clear UI
    setTimeout(() => {
      this.#startFreshSession();
    }, 3000);
  }

  #startStream() {
    if (this.#eventSource) return;
    const url = `${PROXY}/handoff/stream?sessionId=${encodeURIComponent(this.#sessionId)}&since=${this.#lastSeq}`;
    const es = new EventSource(url);
    this.#eventSource = es;

    es.addEventListener('agent_message', (e) => {
      let data;
      try { data = JSON.parse(/** @type {MessageEvent} */ (e).data); } catch { return; }
      const m = data.message;
      if (!m) return;
      this.#lastSeq = Math.max(this.#lastSeq, parseInt(m.id, 10));
      persistSession(this.#sessionId, this.#mode, this.#lastSeq);
      if (m.fromAgent === 'system' && m.text.startsWith('__handoff_ended:')) {
        const reason = m.text.split(':')[1]?.replace(/_/g, ' ').trim();
        this.#exitHandoffMode(reason || 'agent_ended');
        return;
      }
      if (data.mode === 'human' && this.#mode !== 'human') {
        this.#enterHandoffMode('human');
      }
      this.#appendAgentMessage(m.text);
    });

    es.onerror = () => {
      es.close();
      if (this.#eventSource === es) {
        this.#eventSource = null;
        if (this.#mode === 'waiting' || this.#mode === 'human') {
          setTimeout(() => this.#startStream(), 2000);
        }
      }
    };
  }

  #stopStream() {
    if (this.#eventSource) {
      this.#eventSource.close();
      this.#eventSource = null;
    }
  }

  /** @param {string} text */
  #appendAgentMessage(text) {
    const div = document.createElement('div');
    div.className = 'birch-msg birch-msg--agent';
    const inner = document.createElement('div');
    inner.className = 'birch-msg__text';
    inner.textContent = text;
    div.appendChild(inner);
    this.#messageList.appendChild(div);
    this.#scrollMessagesToEnd();
  }

  /**
   * @param {HTMLElement} parentEl
   * @param {Record<string, any>} data
   */
  #renderComparisonTable(parentEl, data) {
    const table = document.createElement('table');
    table.className = 'birch-compare';
    const rows = /** @type {Record<string, any>[]} */ (data.rows ?? []);
    const fields = ['title', 'brand', 'price', 'trust_score', 'certifications'];
    table.innerHTML = `
      <thead><tr><th>Feature</th>${rows.map((r) => `<th>${this.#escapeHtml(r.title ?? r.handle)}</th>`).join('')}</tr></thead>
      <tbody>
        ${fields.map((f) => `
          <tr>
            <td>${f.replace(/_/g, ' ')}</td>
            ${rows.map((r) => `<td>${this.#escapeHtml(String(r[f] ?? '—'))}</td>`).join('')}
          </tr>
        `).join('')}
      </tbody>
    `;
    parentEl.appendChild(table);
  }

  /** @param {unknown} s */
  #escapeHtml(s) {
    /** @type {Record<string, string>} */
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s).replace(/[&<>"']/g, (c) => map[c] ?? c);
  }

  /**
   * Inverse of #escapeHtml, for the handful of entities it produces. The
   * markdown renderer escapes BEFORE it matches links, so a captured href or
   * handle can carry `&amp;` — decode it before encoding it into a URL.
   * @param {string} s
   */
  #unescapeHtml(s) {
    /** @type {Record<string, string>} */
    const map = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
    return String(s).replace(/&(?:amp|lt|gt|quot|#39);/g, (e) => map[e] ?? e);
  }

  /**
   * Canonical storefront path for a product handle — the single place that
   * knows how a handle becomes a URL. Handles are NOT restricted to ASCII:
   * real Birch handles contain `®` (e.g. `rashshield®-diapers`), so the
   * segment is percent-encoded rather than character-filtered. A handle that
   * already arrives encoded is decoded first so it never double-encodes.
   * @param {string} handle
   * @returns {string} e.g. `/products/rashshield%C2%AE-diapers`
   */
  #productUrl(handle) {
    let h = String(handle).trim();
    try {
      const decoded = decodeURIComponent(h);
      if (decoded !== h) h = decoded;
    } catch { /* malformed % sequence — encode the raw string as-is */ }
    return `/products/${encodeURIComponent(h)}`;
  }

  /**
   * Turn a product handle into a human-readable title when we have no card
   * title to fall back on. Strips the trailing numeric id suffix
   * (`-48308306`), swaps separators for spaces, and Title-Cases the words.
   * @param {string} handle
   */
  #prettifyHandle(handle) {
    return handle
      .replace(/-\d+$/, '')
      .replace(/[-_]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /**
   * Minimal, safe markdown renderer for AI assistant text. Handles the
   * subset of CommonMark the model actually produces in answers: bold,
   * italic, inline code, autolinks, unordered/ordered lists, headings,
   * blockquotes, and paragraph breaks. Everything else falls through as
   * escaped text. Crucially: HTML is escaped FIRST so untrusted model
   * output can never inject script/style tags, and only our own emitted
   * tags survive. No external dependency.
   *
   * @param {string} src
   * @returns {string} sanitized HTML
   */
  #renderMarkdown(src) {
    let s = this.#escapeHtml(src);

    // Code fences ```...``` (block) — render before inline so backticks
    // inside aren't reinterpreted.
    s = s.replace(/```([\s\S]*?)```/g, (_m, code) => `<pre class="birch-md-code"><code>${code.replace(/^\n+|\n+$/g, '')}</code></pre>`);

    // Inline code `...`
    s = s.replace(/`([^`\n]+)`/g, '<code class="birch-md-inline-code">$1</code>');

    // Bold **...** (greedy-safe, non-newline)
    s = s.replace(/\*\*([^*\n][^*\n]*?)\*\*/g, '<strong>$1</strong>');
    // Italic *...* (avoid matching ** by requiring single asterisks only)
    s = s.replace(/(^|[^*])\*([^*\n][^*\n]*?)\*(?!\*)/g, '$1<em>$2</em>');

    // Markdown links `[text](target)`. The target is CLASSIFIED, not matched
    // against an allow-list of characters: absolute URL, mail/tel, site-
    // absolute path, or — anything left — a product handle. The old rule
    // allow-listed `[a-z0-9_-]` for handles, which silently rejected the real
    // handles that contain `®` (`rashshield®-diapers` is a live product) and
    // leaked the raw `[Title](handle)` markdown into the bubble. Unsafe or
    // ambiguous targets return the source untouched so the safety net below
    // strips them to their label.
    // One level of nested parens is allowed in the target so `alert(1)` and
    // Wikipedia-style URLs are consumed whole rather than leaving a stray `)`.
    s = s.replace(
      /\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g,
      (m, label, target) => {
        const t = this.#unescapeHtml(String(target));
        // Protocol-relative `//host` is external, not a path — never treat it
        // as site-absolute.
        if (t.startsWith('//')) return m;
        if (/^https?:\/\//i.test(t)) {
          return `<a href="${this.#escapeHtml(t)}" target="_blank" rel="noopener">${label}</a>`;
        }
        if (/^(?:mailto|tel):/i.test(t)) {
          return `<a href="${this.#escapeHtml(t)}">${label}</a>`;
        }
        if (t.startsWith('/')) return `<a href="${this.#escapeHtml(t)}">${label}</a>`;
        // Any other scheme (javascript:, data:) or embedded separator is not a
        // handle — refuse rather than guess.
        if (/[:\\/]/.test(t)) return m;
        return `<a href="${this.#productUrl(t)}">${label}</a>`;
      },
    );

    // Bare product-handle citations `[some-product-handle-1234567]` — the
    // system prompt asks the model to cite products as `[handle]`, so most
    // references arrive WITHOUT the markdown `(url)` part. Rewrite each to a
    // `/products/<handle>` link whose visible text is the product title
    // (resolved from the cards we've seen this session; prettified handle as
    // a fallback). Negative lookahead skips `[handle](…)` already handled above.
    // The character class is deliberately wide (handles may be non-ASCII); the
    // shape checks inside — hyphenated, lowercase, 3+ chars — are what keep
    // ordinary bracketed prose from turning into product links.
    s = s.replace(
      /\[([^\s\]]*-[^\s\]]*)\](?!\()/g,
      (m, handle) => {
        const h = this.#unescapeHtml(String(handle));
        if (h.length < 3 || /[A-Z]/.test(h)) return m;
        const title = this.#titleByHandle.get(h) ?? this.#prettifyHandle(h);
        return `<a href="${this.#productUrl(h)}">${this.#escapeHtml(title)}</a>`;
      },
    );

    // Auto-link bare URLs
    s = s.replace(
      /(^|\s)(https?:\/\/[^\s<]+)/g,
      '$1<a href="$2" target="_blank" rel="noopener">$2</a>',
    );

    // Safety net: anything still wearing `[label](target)` was refused above
    // (unsafe scheme, protocol-relative host, embedded path). Raw markdown
    // syntax must never reach the customer — keep the label, drop the link.
    s = s.replace(/\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g, '$1');

    // Lines → block structure (lists, headings, paragraphs).
    const lines = s.split('\n');
    /** @type {string[]} */
    const out = [];
    /** @type {'ul'|'ol'|null} */
    let listMode = null;
    /** @type {string[]} */
    let paragraph = [];

    const flushParagraph = () => {
      if (paragraph.length === 0) return;
      out.push('<p>' + paragraph.join('<br>') + '</p>');
      paragraph = [];
    };
    const flushList = () => {
      if (listMode) { out.push(`</${listMode}>`); listMode = null; }
    };

    /** Split a pipe-table row, trimming and dropping the leading/trailing
     * pipe artifacts. `| a | b |` → ['a', 'b']. */
    const splitRow = (/** @type {string} */ row) => {
      let r = row.trim();
      if (r.startsWith('|')) r = r.slice(1);
      if (r.endsWith('|')) r = r.slice(0, -1);
      return r.split('|').map((c) => c.trim());
    };

    /** A separator row is the markdown `| --- | :---: | ---: |` line that
     * sits between the header and body of a GFM table. */
    const isSeparator = (/** @type {string} */ row) =>
      /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(row);

    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i] ?? '';
      const line = raw.trimEnd();
      if (line === '') { flushParagraph(); flushList(); continue; }

      // Pipe-table: header line that LOOKS like a row, followed by a
      // separator line of dashes. If the next line isn't a separator we
      // treat the pipe-prefixed line as plain text. Cells preserve inline
      // markdown (bold/italic/code/link) because they were processed before
      // the line-splitting step above.
      if (line.includes('|') && i + 1 < lines.length && isSeparator(lines[i + 1] ?? '')) {
        flushParagraph(); flushList();
        const header = splitRow(line);
        i += 1; // consume the separator
        /** @type {string[][]} */
        const body = [];
        while (i + 1 < lines.length) {
          const peek = (lines[i + 1] ?? '').trimEnd();
          if (peek === '' || !peek.includes('|')) break;
          body.push(splitRow(peek));
          i += 1;
        }
        const thead = '<thead><tr>' + header.map((c) => `<th>${c}</th>`).join('') + '</tr></thead>';
        const tbody = '<tbody>' + body.map((r) =>
          '<tr>' + r.map((c) => `<td>${c}</td>`).join('') + '</tr>'
        ).join('') + '</tbody>';
        out.push(`<table class="birch-md-table">${thead}${tbody}</table>`);
        continue;
      }

      const heading = line.match(/^(#{1,3})\s+(.*)$/);
      if (heading) {
        flushParagraph(); flushList();
        const hashes = heading[1] ?? '';
        const text = heading[2] ?? '';
        const level = hashes.length + 2; // h1→h3, h2→h4, h3→h5 (chat is small)
        out.push(`<h${level} class="birch-md-h">${text}</h${level}>`);
        continue;
      }

      const ul = line.match(/^[-*+]\s+(.*)$/);
      const ol = line.match(/^(\d+)\.\s+(.*)$/);
      if (ul) {
        flushParagraph();
        if (listMode !== 'ul') { flushList(); out.push('<ul class="birch-md-list">'); listMode = 'ul'; }
        out.push(`<li>${ul[1] ?? ''}</li>`);
        continue;
      }
      if (ol) {
        flushParagraph();
        // start= preserves the real number so a list broken across an
        // interruption (e.g. an indented description line) keeps counting
        // 1,2,3 instead of every fresh <ol> restarting at 1.
        if (listMode !== 'ol') { flushList(); out.push(`<ol class="birch-md-list" start="${parseInt(ol[1] ?? '1', 10) || 1}">`); listMode = 'ol'; }
        out.push(`<li>${ol[2] ?? ''}</li>`);
        continue;
      }

      // Indented continuation of the current list item (a wrapped/description
      // line under "1. …"). Fold it into the preceding <li> so the list isn't
      // split into separate <ol>s — keeping the numbering contiguous.
      if (listMode && /^\s+\S/.test(raw) && out.length && /^<li>/.test(out[out.length - 1] ?? '')) {
        out[out.length - 1] = (out[out.length - 1] ?? '').replace(/<\/li>$/, '<br>' + line.trim() + '</li>');
        continue;
      }

      const bq = line.match(/^>\s?(.*)$/);
      if (bq) {
        flushParagraph(); flushList();
        out.push(`<blockquote class="birch-md-bq">${bq[1] ?? ''}</blockquote>`);
        continue;
      }

      flushList();
      paragraph.push(line);
    }
    flushParagraph(); flushList();

    return out.join('');
  }

  /**
   * Unified `tool:cart_updated` handler — single owner of cart-side-effects:
   *   1. Tell the theme to refresh its cart bubble + drawer (`cart:refresh`).
   *   2. Persist a 'cart_updated' entry in #displayLog so a refresh
   *      restores the success receipt under the AI's hedged confirmation.
   *
   * Called from two paths:
   *   - SSE-side when the worker emits `tool:cart_updated` over the stream
   *     (future Plus / B2B cart flows).
   *   - Frontend AJAX success path (#executeCartAction calls this directly
   *     with a synthetic event after a successful /cart/*.js POST).
   *
   * Declared as an arrow-function field (vs. a class method) so the type
   * checker resolves it consistently regardless of declaration order.
   *
   * @type {(data: Record<string, unknown>) => Promise<void>}
   */
  #handleCartUpdated = async (data) => {
    // Theme convention is `cart:update` with a specific detail shape (see
    // assets/cart-restore.js for the canonical example). The detail.resource
    // is the live cart from /cart.js; the bubble + drawer listen for this
    // and re-render their state.
    let resource = null;
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (r.ok) resource = await r.json();
    } catch { /* fall through with null resource */ }

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource,
        sourceId: 'birch-ai',
        data: {
          source: 'birch-ai',
          // Not `item_count` — the bubble counts a bundle once. See cart-count.js.
          ...(resource && typeof resource === 'object' && 'item_count' in resource && {
            itemCount: cartDisplayCount(resource),
          }),
        },
      },
    }));

    const action = String(data.action ?? 'update');
    this.#displayLog.push({ kind: 'cart_updated', action, ts: Date.now() });
    saveDraft(this.#sessionId, this.#displayLog);
  };

  /**
   * Slice 8 — execute the cart mutation requested by the AI. The worker
   * validated the input and emitted `tool:cart_action`; we run the actual
   * Shopify AJAX here (browser carries the cart cookie). On success we
   * emit a synthetic `tool:cart_updated` so `#handleCartUpdated` runs.
   *
   * @param {Record<string, unknown>} data
   */
  async #executeCartAction(data) {
    const action = String(data.action ?? '');
    // Slice 8 (refined): show an immediate pending indicator so the user sees
    // the action is in flight. Replaced by either a silent success (cart
    // bubble updates via cart:refresh) or a visible red error receipt. The
    // AI's hedged message ("I'm adding it now…") sits above and remains
    // truthful in both outcomes.
    const pending = document.createElement('div');
    pending.className = 'birch-receipt birch-receipt--pending';
    pending.textContent = action === 'add' ? 'Adding to cart…'
      : action === 'add_bundle' ? 'Adding the bundle…'
      : action === 'remove' ? 'Removing from cart…'
      : action === 'discount' ? 'Applying discount…'
      : 'Updating cart…';
    /** Set by a branch that finished in a state the standard receipt would overstate. */
    let receiptOverride = '';
    this.#messageList.appendChild(pending);
    this.#scrollMessagesToEnd();

    try {
      if (action === 'add') {
        const items = /** @type {Array<{handle: string, quantity: number, variant_id?: string}>} */
          (Array.isArray(data.items) ? data.items : []);
        // An explicit variant_id wins: it is either the combination the customer
        // named ("size 4, diapers only") or the one they have selected on the
        // page. Falling through to first-available for those put the WRONG size
        // in the cart — and on a product whose variants differ in price, at the
        // wrong price too.
        //
        // Handles still resolve to first-available, which is what the
        // storefront's own "Add to cart" button does. The id is verified
        // against the product's real variants rather than posted blind, so a
        // stale or mismatched id degrades to that same fallback instead of a
        // cart error.
        const resolved = await Promise.all(items.map(async (i) => {
          const r = await fetch(`${this.#productUrl(i.handle)}.js`);
          if (!r.ok) throw new Error(`product lookup failed: ${i.handle}`);
          const product = await r.json();
          const variants = product.variants || [];
          const asked = i.variant_id
            ? variants.find((/** @type {any} */ v) => String(v.id) === String(i.variant_id))
            : null;
          const variant = asked
            || variants.find((/** @type {any} */ v) => v.available)
            || variants[0];
          if (!variant) throw new Error(`no variant for ${i.handle}`);
          return { id: variant.id, quantity: i.quantity };
        }));
        const addResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: resolved }),
        });
        if (!addResp.ok) {
          const err = await addResp.json().catch(() => ({}));
          throw new Error(err.description || `cart add failed (${addResp.status})`);
        }
      } else if (action === 'add_bundle') {
        // A bundle is added by pressing the page's own Add to cart button, never by
        // posting a payload from here: the multi-line body carries properties the
        // bundles app's discount Function validates, and assets/bundle-pdp.js owns
        // that contract. It also verifies the add actually landed and tells us when
        // it didn't — an under-filled build-your-own box is refused by the app with
        // no request at all, and that must not read back as success.
        const api = /** @type {any} */ (window).BirchAI?.bundle;
        if (!api || typeof api.addToCart !== 'function') {
          throw new Error('this page can’t add the bundle from chat — use the Add to cart button');
        }
        const result = await api.addToCart(Number(data.copies) || 1);
        if (!result?.ok) {
          throw new Error(
            result?.reason === 'incomplete'
              ? 'the bundle isn’t finished — finish picking, then add it'
              : 'the bundle didn’t make it into the cart'
          );
        }
        // The cart could not be read across the click, so the add is unconfirmed.
        // Say that rather than promote a hopeful click to a fact.
        if (result.verified === false) receiptOverride = '✓ Added — check your cart to confirm';
      } else if (action === 'remove') {
        // `lineKeys` removes a whole bundle: one box is several cart lines sharing an
        // `_ssBundleKey`, and pulling one out leaves the rest priced as a box that no
        // longer exists. Sequential because cart mutations race; keyed rather than
        // indexed, so the keys still resolve after earlier lines are gone.
        const lineKeys = Array.isArray(data.lineKeys) && data.lineKeys.length > 0
          ? data.lineKeys.map((k) => String(k)).filter(Boolean)
          : [String(data.lineKey ?? '')].filter(Boolean);
        if (lineKeys.length === 0) throw new Error('missing lineKey');
        for (const lineKey of lineKeys) {
          const r = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: 0 }),
          });
          if (!r.ok) throw new Error(`cart remove failed (${r.status})`);
        }
      } else if (action === 'discount') {
        const code = String(data.code ?? '');
        if (!code) throw new Error('missing code');
        // Shopify's standard pattern: visit /discount/{code} to apply.
        // We use a no-redirect fetch so the user stays in the chat. Shopify
        // sets the discount cookie regardless and the next /cart.js read
        // reflects it.
        await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, { credentials: 'same-origin' }).catch(() => {});
      } else {
        console.warn('[birch-ai-companion] unknown cart action:', action);
        return;
      }
      // Success — replace the pending indicator with a check, then route
      // through #handleCartUpdated via a synthetic tool:cart_updated. This
      // keeps a single owner for the canonical `cart:update` dispatch (with
      // proper detail.resource shape) + displayLog persistence + future
      // telemetry, regardless of who emitted the event.
      pending.classList.add('birch-receipt--success');
      pending.classList.remove('birch-receipt--pending');
      pending.textContent = receiptOverride
        || (action === 'add' ? '✓ Added to your cart'
          : action === 'add_bundle' ? '✓ Bundle added to your cart'
          : action === 'remove' ? '✓ Removed from your cart'
          : action === 'discount' ? '✓ Discount applied (check the cart total)'
          : '✓ Cart updated');
      void this.#handleCartUpdated({ source: 'frontend-ajax', action, ...data });
    } catch (err) {
      console.error('[birch-ai-companion] cart action failed:', err instanceof Error ? err.message : String(err));
      // Honest failure — replace the pending indicator with a red error
      // receipt. The AI's hedged "I'm adding it now…" message above stays
      // truthful (it never claimed the action completed).
      pending.classList.add('birch-receipt--error');
      pending.classList.remove('birch-receipt--pending');
      // A bundle failure is reported in its own words: "the bundle isn't finished"
      // tells the shopper what to do next, where the generic line would send them
      // to the cart page to fix something that isn't wrong with their cart.
      pending.textContent = action === 'add_bundle'
        ? `Couldn't add the bundle — ${err instanceof Error ? err.message : 'try the Add to cart button on this page'}.`
        : `Couldn't ${action === 'add' ? 'add to' : action === 'remove' ? 'remove from' : 'apply discount to'} cart — try again from the cart page.`;
      this.#scrollMessagesToEnd();
    }
  }
}

customElements.define('birch-ai-companion', BirchAICompanion);

// Auto-open the chat after auto-redirect (item #6 — chat keeps streaming
// description while the search page shows the cards). Exception: a mobile
// "Open full page" tap tags the URL with chat=min because the full-screen
// panel would cover the results — honor that and skip the auto-open.
window.addEventListener('DOMContentLoaded', () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'ai' && params.get('session') && params.get('chat') !== 'min') {
      // The trigger handler is a bare toggle and connectedCallback may already
      // have restored the panel open from the persisted state — clicking then
      // would close it. Only click when it is actually closed.
      const panel = /** @type {HTMLElement | null} */ (document.querySelector('birch-ai-companion .birch-panel'));
      if (!panel || panel.hidden) {
        const trigger = /** @type {HTMLElement | null} */ (document.querySelector('[data-ai-trigger]'));
        if (trigger) trigger.click();
      }
    }
  } catch { /* ignore */ }
});

// Expose helpers needed by other widgets to keep them coordinated. Anyone
// dispatching `birch:open-with-query` with `{ query, fresh: true }` opens the
// chat with a fresh session and submits the query.
/** @type {any} */ (window).BirchAIChat = {
  /** @param {string} query */
  openWithQuery(query) {
    window.dispatchEvent(new CustomEvent('birch:open-with-query', { detail: { query, fresh: true } }));
  },
};
